/* r3-pdp2: one purchase-options state model for the product page and the configure dialog.
   product-form.js alone owns the cart request; this element only keeps the form honest. */

/* r5 item 15: thumbnail rail shared by the product page and the configure dialog (both load this file).
   The track scrolls natively (touch, trackpad, keyboard focus) with its scrollbar hidden; the rail
   marks overflow and both ends so CSS can fade the edges and show the small arrow buttons. */
(() => {
  if (customElements.get('cq-thumb-rail')) return;
  customElements.define('cq-thumb-rail', class extends HTMLElement {
    connectedCallback() {
      if (this.cqEvents) return;
      this.track = this.querySelector('[data-cq-rail-track]');
      if (!this.track) return;
      this.cqEvents = new AbortController();
      const options = { signal: this.cqEvents.signal };
      this.addEventListener('click', (event) => {
        const arrow = event.target.closest('[data-cq-rail-dir]');
        if (!arrow) return;
        event.preventDefault();
        this.page(Number(arrow.dataset.cqRailDir));
      }, options);
      this.track.addEventListener('scroll', () => this.schedule(), { ...options, passive: true });
      // The observer's first callback schedules the first update; measuring here forced a layout during parsing (A7 4c).
      this.resize = new ResizeObserver(() => this.schedule());
      this.resize.observe(this.track);
    }

    disconnectedCallback() {
      this.cqEvents?.abort();
      this.cqEvents = null;
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
    }

    schedule() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }

    update() {
      const track = this.track;
      const max = track.scrollWidth - track.clientWidth;
      const position = Math.abs(track.scrollLeft);
      const overflow = max > 1;
      this.toggleAttribute('data-overflow', overflow);
      this.toggleAttribute('data-at-start', !overflow || position <= 1);
      this.toggleAttribute('data-at-end', !overflow || position >= max - 1);
    }

    page(direction) {
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const distance = Math.max(this.track.clientWidth * 0.75, 72) * direction * (rtl ? -1 : 1);
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({ left: distance, behavior: reduce ? 'auto' : 'smooth' });
    }
  });
})();

/* r12 item 3: the same subscription explanation on the product page and in Configure. A manual popover escapes
   the dialog's scrolling/clipping ancestors without moving markup outside its accessible description relationship. */
(() => {
  if (customElements.get('cq-subscription-info')) return;
  customElements.define('cq-subscription-info', class extends HTMLElement {
    connectedCallback() {
      if (this.cqEvents) return;
      this.button = this.querySelector('button');
      this.tip = this.querySelector('[role="tooltip"]');
      if (!this.button || !this.tip) return;
      this.cqEvents = new AbortController();
      const options = { signal: this.cqEvents.signal };
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'touch') this.show();
      }, options);
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        this.closeTimer = setTimeout(() => {
          if (!this.contains(document.activeElement) && !this.matches(':hover')) this.hide();
        }, 120);
      }, options);
      this.addEventListener('focusin', () => this.show(), options);
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget) && !this.matches(':hover')) this.hide();
      }, options);
      this.button.addEventListener('pointerdown', () => { this.pointerWasOpen = !this.tip.hidden; }, options);
      this.button.addEventListener('click', () => {
        const close = this.pointerWasOpen ?? !this.tip.hidden;
        this.pointerWasOpen = undefined;
        if (close) this.hide(); else this.show();
      }, options);
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.hide();
      }, { ...options, capture: true });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || this.tip.hidden) return;
        event.preventDefault();
        event.stopPropagation();
        this.hide();
      }, { ...options, capture: true });
      const position = () => {
        if (this.tip.hidden) return;
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.position());
      };
      document.addEventListener('scroll', position, { ...options, capture: true, passive: true });
      window.addEventListener('resize', position, options);
      window.visualViewport?.addEventListener('resize', position, options);
      window.visualViewport?.addEventListener('scroll', position, options);
      this.closest('dialog')?.addEventListener('close', () => this.hide(), options);
      // A switch to One-time must also dismiss a tooltip which is currently in the top layer.
      this.visibility = new MutationObserver(() => {
        if (this.closest('[hidden]')) this.hide();
      });
      const plan = this.closest('[data-cq-plan-wrap]');
      if (plan) this.visibility.observe(plan, { attributes: true, attributeFilter: ['hidden'] });
    }

    disconnectedCallback() {
      this.hide();
      this.cqEvents?.abort();
      this.cqEvents = null;
      this.visibility?.disconnect();
    }

    show() {
      clearTimeout(this.closeTimer);
      if (this.closest('[hidden]')) return;
      this.tip.hidden = false;
      if (this.tip.showPopover && !this.tip.matches(':popover-open')) this.tip.showPopover();
      this.position();
    }

    hide() {
      clearTimeout(this.closeTimer);
      cancelAnimationFrame(this.frame);
      if (!this.tip) return;
      if (this.tip.hidePopover && this.tip.matches(':popover-open')) this.tip.hidePopover();
      this.tip.hidden = true;
    }

    position() {
      if (this.tip.hidden) return;
      if (!this.button.getClientRects().length) { this.hide(); return; }
      const viewport = window.visualViewport;
      const leftEdge = (viewport?.offsetLeft || 0) + 12;
      const topEdge = (viewport?.offsetTop || 0) + 12;
      const width = viewport?.width || document.documentElement.clientWidth;
      const height = viewport?.height || window.innerHeight;
      this.tip.style.maxWidth = `${Math.max(0, width - 24)}px`;
      this.tip.style.maxHeight = `${Math.max(0, height - 24)}px`;
      const button = this.button.getBoundingClientRect();
      const tip = this.tip.getBoundingClientRect();
      const left = Math.max(leftEdge, Math.min(button.left, leftEdge + width - 24 - tip.width));
      // V0 #32: placed from the plan line the icon sits in (it wraps to two lines on phones), never over it; above
      // the line when below would cover Add to cart or leave the screen.
      const line = this.closest('.cq-purchase-plan-note')?.getBoundingClientRect();
      const lineTop = Math.min(button.top, line?.top ?? button.top);
      const below = Math.max(button.bottom, line?.bottom || 0) + 8;
      const above = lineTop - tip.height - 8;
      const add = [...(this.closest('cq-purchase-options')?.querySelectorAll('.cq-purchase-add') || [])]
        .map((element) => element.getBoundingClientRect()).find((box) => box.height);
      const coversAdd = Boolean(add) && add.top < below + tip.height && add.bottom > below;
      const fitsBelow = below + tip.height <= topEdge + height - 24;
      const top = fitsBelow && (!coversAdd || above < topEdge) ? below : above;
      this.tip.style.left = `${left}px`;
      this.tip.style.top = `${Math.max(topEdge, Math.min(top, topEdge + height - 24 - tip.height))}px`;
    }
  });
})();

/* r7 (AJ 2026-10-08): which photos the selected option shows, on the product page and in the Configure popup.
   snippets/cq-variant-photos.liquid renders the page-load state with the same rules; keep the two in step.
   Each photo element carries data-media-id (or data-configure-media), data-cq-pos (its place in Shopify's
   media list), data-cq-group (the option photo that starts its group, 0 before the first one) and
   data-cq-vphoto (an option's own photo). The gallery's data-cq-photos names the rule:
   - group (tag variant-media, PR #67): the option's group, then the shared photos after photo 1;
   - featured (automatic): the option's photo first, other options' own photos hidden, all others kept;
   - option (round 4 fix pass, "Group photos by option"): data-cq-group holds the values a photo names (".4.", "-"
     for a shared photo, "x" for unsold values only); the variant's own photo first when it fits, then the photos
     naming the chosen value, then the shared ones. The gallery's data-cq-photo-option (the option's index) and
     data-cq-photo-values (its values, lowercase) give the chosen value (pick);
   - all: every photo (an option change only moves to its photo). */
window.CQVariantPhotos = window.CQVariantPhotos || {
  read(element) {
    return {
      id: element.dataset.mediaId || element.dataset.configureMedia,
      pos: Number(element.dataset.cqPos),
      group: element.dataset.cqGroup || '0',
      vphoto: element.hasAttribute('data-cq-vphoto'),
    };
  },

  // Group mode: exact variant ID for explicit guide companions. Option mode: chosen value's index.
  pick(gallery, variant) {
    if (gallery?.dataset.cqPhotos === 'group') return variant?.id == null ? '' : String(variant.id);
    const index = gallery?.dataset.cqPhotoOption;
    if (index === undefined || index === '' || !Array.isArray(variant?.options)) return '';
    let values;
    try { values = JSON.parse(gallery.dataset.cqPhotoValues || '[]'); } catch { return ''; }
    const at = values.indexOf(String(variant.options[Number(index)] ?? '').trim().toLowerCase());
    return at < 0 ? '' : String(at);
  },

  // Returns the media ids to show, in order. head: the selected option's media id (empty when it has none);
  // pick: exact variant ID in group mode; option mode's chosen value (see pick).
  plan(items, mode, head, pick = '', displayOrder = '') {
    const all = [...items].sort((a, b) => a.pos - b.pos);
    const lead = head ? String(head) : '';
    let shown;
    if (mode === 'option') {
      // Same rule as snippets/cq-variant-photos.liquid; nothing to show: the variant's photo, else photo 1, alone.
      const key = pick === '' || pick == null ? '' : `.${pick}.`;
      const named = (item) => key !== '' && item.group.includes(key);
      const own = lead ? all.find((item) => item.id === lead && (item.group === '-' || named(item))) : undefined;
      const rest = all.filter((item) => item !== own);
      shown = (own ? [own] : []).concat(rest.filter(named), rest.filter((item) => item.group === '-'));
      if (!shown.length) shown = [all.find((item) => item.id === lead) || all[0]].filter(Boolean);
    } else if (mode === 'group') {
      const owned = (item) => lead !== '' && item.group.split('~')[0] === lead;
      const companion = (item) => pick !== '' && pick != null && item.group.split('~').slice(1).includes(String(pick));
      // Keep the selected pack's group first even when its guide appeared earlier in Shopify's gallery.
      shown = all.filter(owned)
        .concat(all.filter((item) => !owned(item) && companion(item)), all.filter((item) => item.group === '0' && item.pos > 0));
      // Nothing for this option: PR #67 showed photo 1 alone.
      if (!shown.length) shown = all.slice(0, 1);
    } else {
      shown = mode === 'featured' ? all.filter((item) => !item.vphoto || item.id === lead) : all;
      if (!shown.length) shown = all;
      const first = shown.find((item) => item.id === lead);
      if (first) shown = [first, ...shown.filter((item) => item !== first)];
    }
    if (displayOrder && shown.length > 1) {
      const [position, guideIds = '', frontIds = '', nonimageIds = '', sequenceIds = ''] = displayOrder.split(';');
      const ids = (list) => new Set(list.split(',').filter(Boolean));
      const guides = ids(guideIds), fronts = ids(frontIds), nonimages = ids(nonimageIds);
      const sequence = [...ids(sequenceIds)];
      const [primary, ...rest] = shown;
      const guidePosition = ['after_primary', 'after_photos'].includes(position) && rest.some((item) => guides.has(item.id)) ? position : '';
      const guide = (item) => guidePosition !== '' && guides.has(item.id);
      const front = (item) => !guide(item) && fronts.has(item.id);
      const nonimage = (item) => guidePosition === 'after_photos' && nonimages.has(item.id);
      const sequenced = sequence.map((id) => rest.find((item) => item.id === id && !guide(item) && !item.vphoto)).filter(Boolean);
      const sequencedIds = new Set(sequenced.map((item) => item.id));
      const remaining = rest.filter((item) => !guide(item) && !front(item) && !nonimage(item) && !sequencedIds.has(item.id));
      const frontDetails = rest.filter((item) => front(item) && !sequencedIds.has(item.id));
      // Visibility and owner groups are already settled. Never pull hidden media into the display sequence.
      shown = guidePosition === 'after_primary'
        ? [primary, ...rest.filter(guide), ...sequenced, ...frontDetails, ...remaining]
        : [primary, ...sequenced, ...frontDetails, ...remaining, ...rest.filter(guide), ...rest.filter(nonimage)];
    }
    return shown.map((item) => item.id);
  },

  // Puts each element in plan order (hidden ones after, in Shopify's order) and hides the rest.
  // Only moves elements when the order changed, so a playing video is not restarted for nothing.
  arrange(container, elements, ids) {
    if (!container || !elements.length) return;
    const rank = new Map(ids.map((id, index) => [String(id), index]));
    const place = (element) => {
      const id = element.dataset.mediaId || element.dataset.configureMedia;
      return rank.has(id) ? rank.get(id) : ids.length + Number(element.dataset.cqPos);
    };
    const sorted = [...elements].sort((a, b) => place(a) - place(b));
    sorted.forEach((element) => element.classList.toggle('cq-photo-off', place(element) >= ids.length));
    // P1: the product page's phone carousel keeps its slides in its own track, where the photos hidden at page load
    // are not yet; every element is moved in when one is missing.
    const current = [...container.children].filter((child) => sorted.includes(child));
    if (current.length !== sorted.length || current.some((element, index) => element !== sorted[index])) container.append(...sorted);
  },

  // Starts loading the photo about to lead (high priority) and waits for it briefly, so the switch rarely shows an
  // empty tile (the product page shows the thumbnail behind it until it arrives, assets/media-gallery.js).
  ready(element) {
    const image = element?.querySelector('img');
    if (!image || (image.complete && image.naturalWidth)) return Promise.resolve();
    image.fetchPriority = 'high';
    image.loading = 'eager';
    return Promise.race([
      image.decode().catch(() => {}),
      new Promise((resolve) => { setTimeout(resolve, 400); }),
    ]);
  },
};

/* r10 item 4: Shop Pay Installments under the price takes the cart page's look (round 9, sections/main-cart-items.liquid,
   the same CSS): our Lato at 13 px in the muted grey, the Shop logo at text height on the baseline and a 14 px info
   icon in a 24 px target. Shopify draws the line in its own open shadow root, in its own font (GTStandard, a system
   sans when not loaded) with a 14 px logo and a 20 px icon, so one small style element goes into that root, before
   the widget first paints where possible (the font is then never swapped under the shopper's eyes). Only the widgets
   in the price block are styled here; the cart keeps its own copy. */
window.CQPurchaseTerms = window.CQPurchaseTerms || (() => {
  const css = '#shopify-installments{font-family:inherit!important;font-size:inherit!important;line-height:20px!important;color:inherit!important}'
    + '#shopify-installments .font-bold{font-weight:600}'
    + '#shopify-installments-content>div{display:inline-block!important;vertical-align:-0.185em!important;margin:0 1px}'
    + '#shopify-installments-content>div>svg{display:block;height:11.5px!important;width:auto!important}'
    + '#shopify-installments-cta{display:inline-grid!important;place-items:center;width:24px;height:24px;margin:-2px 0 -2px -2px!important;padding:0!important;vertical-align:middle;color:inherit;border-radius:6px}'
    + '#shopify-installments-cta>svg{position:static!important;width:14px!important;height:14px!important}';
  const style = (scope = document) => {
    scope.querySelectorAll('.cq-purchase-terms shopify-payment-terms').forEach((element) => {
      const root = element.shadowRoot;
      if (!root || root.querySelector('style[data-cq-terms]')) return;
      const tag = document.createElement('style');
      tag.setAttribute('data-cq-terms', '');
      tag.textContent = css;
      root.append(tag);
    });
  };
  customElements.whenDefined('shopify-payment-terms').then(() => style());
  return { style };
})();

/* One post-paint measurement queue for the layout reads this file makes after a change: the option guides' fit check,
   the Add to cart fit and the SKU row. A caller queues a key with callbacks. One hop later (a frame, then a task, so
   the change has painted) the queue runs every entry's clear step (writes a measurement needs out of the way first),
   then every read, then every write. Reads of a page whose layout is clean cost nothing; a read after a write lays the
   whole page out again, which six guides and two fit checks used to do one after another, each in its own task. A key
   queued again before the hop replaces its earlier entry. A write that needs the page as it is after the writes queues
   a follow-up read and write pair (after), which run as one more read pass and write pass in the same hop. */
window.CQMeasure = window.CQMeasure || (() => {
  const pending = new Map();
  let followUps = null;
  let scheduled = false;
  const run = (entries) => {
    const results = entries.map((entry) => (entry.read ? entry.read() : undefined));
    followUps = [];
    entries.forEach((entry, index) => { if (entry.write) entry.write(results[index]); });
    const next = followUps;
    followUps = null;
    if (next.length) run(next);
  };
  const flush = () => {
    scheduled = false;
    const entries = [...pending.values()];
    pending.clear();
    entries.forEach((entry) => { if (entry.clear) entry.clear(); });
    run(entries);
  };
  return {
    queue(key, { clear, read, write }) {
      pending.set(key, { clear, read, write });
      if (scheduled) return;
      scheduled = true;
      // A hidden page has no frames, so the hop falls back to a task.
      if (document.hidden || typeof requestAnimationFrame !== 'function') setTimeout(flush, 0);
      else requestAnimationFrame(() => setTimeout(flush, 0));
    },
    cancel(key) {
      pending.delete(key);
    },
    // From a write callback: one more read and write once every write of the current pass has landed.
    after(read, write) {
      if (followUps) followUps.push({ read, write });
      else this.queue({}, { read, write });
    },
  };
})();

(() => {
  if (customElements.get('cq-purchase-options')) return;
  const normalized =(value) => String(value ?? '').replace(/[“”″]/g, '"').replace(/[‘’]/g, "'").trim();
  // A variant's option values normalized once (connectedCallback fills variant.norm; any other variant object gets it
  // on first use), and an option input's normalized value, cached on the input.
  const norms = (variant) => variant.norm || (variant.norm = (variant.options || []).map(normalized));
  const normInput = (input) => input.cqNorm ?? (input.cqNorm = normalized(input.value));
  // Writes text only when it differs, so live regions and observers see real changes only.
  // r10 item 6 switch callouts, off since AJ 2026-10-18 15:06.
  const SWITCH_CALLOUTS = false;
  const setText = (element, text) => { if (element && element.textContent !== text) element.textContent = text; };
  // Resolves after the next frame has painted (a frame, then a task), so work queued on it never delays a tap's paint.
  // A hidden page has no frames, so it falls back to a task.
  const afterPaint = () => new Promise((resolve) => {
    if (document.hidden || typeof requestAnimationFrame !== 'function') setTimeout(resolve, 0);
    else requestAnimationFrame(() => setTimeout(resolve, 0));
  });

  // r8 item 21: money formatting identical to the Liquid money filter. Copied from assets/custom-bundle.js (shape,
  // deriveMoneyFormat, formatMoney; that file only loads on the bundle section). The format is derived from two
  // Liquid-rendered probes on the element: {{ 100 | money }} and {{ 123456789 | money }}.
  const shape = (text) => {
    if (typeof text !== 'string') return null;
    const first = text.search(/\d/);
    if (first === -1) return null;
    let last = text.length - 1;
    while (last >= 0 && !/\d/.test(text[last])) last -= 1;
    return { prefix: text.slice(0, first), body: text.slice(first, last + 1), suffix: text.slice(last + 1) };
  };
  const deriveMoneyFormat = (unitProbe, groupedProbe) => {
    const unit = shape(unitProbe);
    const grouped = shape(groupedProbe);
    if (!unit || !grouped) return null;
    const unitSeps = unit.body.match(/\D/g) || [];
    if (unitSeps.length > 1) return null;
    const decimalSep = unitSeps[0] || '';
    const intPart = decimalSep ? unit.body.split(decimalSep)[0] : unit.body;
    const decPart = decimalSep ? unit.body.split(decimalSep)[1] || '' : '';
    const decimals = decPart.length;
    if (decimals > 3) return null;
    const rendered = Number(`${intPart}.${decPart || '0'}`);
    if (!rendered) return null;
    const divisor = Math.round(100 / rendered);
    if (divisor !== 1 && divisor !== 100 && divisor !== 1000) return null;
    let groupedBody = grouped.body;
    if (decimals > 0) groupedBody = groupedBody.slice(0, -(decimals + decimalSep.length));
    const thousandsSep = (groupedBody.match(/\D/g) || [])[0] || '';
    return { prefix: unit.prefix, suffix: unit.suffix, decimals, decimalSep, thousandsSep, divisor };
  };
  const formatMoney = (minorUnits, fmt) => {
    if (!fmt) {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(minorUnits / 100);
    }
    const value = minorUnits / fmt.divisor;
    const sign = value < 0 ? '-' : '';
    const parts = Math.abs(value).toFixed(fmt.decimals).split('.');
    const grouped = fmt.thousandsSep ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, fmt.thousandsSep) : parts[0];
    const body = parts[1] ? grouped + fmt.decimalSep + parts[1] : grouped;
    return sign + fmt.prefix + body + fmt.suffix;
  };
  const ONE_TIME = ['_scLineItemOneTime', '_scProductOneTime', '_scOrderOneTime'];
  // r8 item 29: a single-choice add-on group with more than this many values starts collapsed to a summary row.
  // The addendum says "more than 6"; no Bold group on the store has more than 5 (census 2026-10-09), and the spec's
  // acceptance expects the 5-faucet Thunder group to collapse, so the line is drawn after 4. One constant.
  const COLLAPSE_AFTER = 4;
  const MULTI_TYPES = /^(?:checkboxmulti|swatch_multi)$/;
  // A Bold group with a value list that enhanceGroups has not marked yet (it marks every such group once).
  const NEW_GROUP = '.bold_option:not([data-cq-collapse]) > .bold_option_element';
  // r10 item 9: a group enhanceToggles has not looked at yet (it marks every group once; Bold drawing a group again
  // makes a new, unmarked node), so a folded group is never painted open first.
  const NEW_TOGGLES = '.bold_option:not([data-cq-toggle-seen])';
  // r10 item 9: post-filters by add-on name (UV sterilizer, remineralizer, alkalizer), a "No ..." value, "(Optional)".
  const POST_FILTER = /\b(?:uv|ultraviolet|steriliz|remineraliz|alkaliz)/i;
  const NONE_VALUE = /^(?:no|none)\b/i;
  // Release 1 lane Y (AJ 2026-10-11 05:36: "when the dont have a faucet selected can you make it say Faucets Style then
  // No faucet kit will be included"): the Thunder faucet group, Bold's "Faucets Style" (census 2026-10-11: the same
  // swatch group on all 16 Thunder systems), by its Bold title.
  const FAUCET_STYLE = /^faucets?\s+styles?\b/i;
  const OPTIONAL_SUFFIX = /\s*\(optional\)\s*$/i;
  // A check box add-on's product photo (Bold has none for it), fetched once per product handle.
  const togglePhotos = new Map();
  const togglePhoto = (handle) => {
    if (!togglePhotos.has(handle)) {
      togglePhotos.set(handle, fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .then((product) => {
          const src = product?.featured_image || '';
          if (!src) return '';
          const url = new URL(src.startsWith('//') ? `https:${src}` : src, location.origin);
          url.searchParams.set('width', '112');
          return url.href;
        })
        .catch(() => ''));
    }
    return togglePhotos.get(handle);
  };
  // A trailing bracketed part of a value name: "Chrome Faucet with 1/4" Tube (European Handle)".
  const BRACKET = /^(.*\S)\s*\(([^()]+)\)$/;
  // A slash between two letters ("Alkalizer/Remineralizer"), never a fraction ("1/4").
  const SLASH = /\p{L}\/\p{L}/u;
  const SLASH_G = /(\p{L})\/(?=\p{L})/gu;
  const cleanText = (element) => (element?.textContent || '').replace(/\s+/g, ' ').trim();
  // r8 item 24: a trailing colon on a Bold group title, before an optional required marker ("Name:" or "Name:*").
  const TITLE_COLON = /:(\s*\**\s*)$/;
  // AJ 2026-10-11 22:37 ET (option A: "ill update loop so its not default monthly then just use the default"): the
  // frequency to start on is Loop's default for the plan group, the rule of snippets/cq-preferred-selling-plan.liquid.
  // In the variant's first plan group (groups: the product's group order), the plan Loop marks as that group's default
  // (Loop's live file once read, else the theme's copy; assets/cq-loop-default.js), else the group's first plan (plans
  // arrive in Loop's order). It replaces the r5 item 17 rule (6 months, then 12 months, then the first plan).
  // Product/card Liquid may name an existing category default (faucet cartridges: six months); it precedes Loop.
  const preferredPlan = (plans, groups = [], categoryDefaultPlan = '') => {
    if (!plans.length) return null;
    const categoryPlan = plans.find((plan) => String(plan.id) === String(categoryDefaultPlan));
    if (categoryPlan) return categoryPlan;
    const group = groups.map((item) => String(item.id)).find((id) => plans.some((plan) => String(plan.group) === id)) ?? String(plans[0].group);
    const loop = window.CQLoopDefaults;
    return loop?.pick(plans, group, loop.live() || loop.snapshot) || plans.find((plan) => String(plan.group) === group) || plans[0];
  };

  // Shared option labels explain dependencies and subscription requirements, without relative price differences.
  // Keep selection resolution and selling-plan choice outside this pure presentation function.
  const optionChoiceNote = (current, candidate, index, plan, candidateRequiresPlan, optionNames, copy) => {
    const empty = { text: '', spoken: '', delta: false };
    if (!current || !candidate) return empty;
    const currentNorm = norms(current);
    const candidateNorm = norms(candidate);
    if (candidateNorm[index] === currentNorm[index]) return empty;
    const changed = candidate.options.flatMap((value, i) => i !== index && candidateNorm[i] !== currentNorm[i]
      ? [copy.optionChanges.replace('__OPTION__', optionNames[i]).replace('__VALUE__', value)] : []);
    if (changed.length) {
      const text = changed.join(copy.optionChangesSeparator);
      const visible = candidateNorm[index] === normalized(copy.optionDependencyCueValue);
      return { text: visible ? text : '', spoken: text, delta: false };
    }
    if (current.callForPrice || candidate.callForPrice) return empty;
    if (plan) {
      const matching = candidate.plans.find((item) => String(item.id) === String(plan.id));
      if (!matching) return { text: copy.optionTermsChange, spoken: copy.optionTermsChange, delta: false };
      if (!plan.perDelivery || !matching.perDelivery) return { text: copy.optionReviewPlan, spoken: copy.optionReviewPlan, delta: false };
    } else if (candidateRequiresPlan) {
      return { text: copy.optionRequiresPlan, spoken: copy.optionRequiresPlan, delta: false };
    }
    return empty;
  };

  // r6 item 11, r10 item 1: the column rule (written out in snippets/cq-option-picker.liquid) for values the Liquid
  // cannot lay out because the Bold Product Options app renders them in the browser. Same rule as the Liquid, with
  // real text widths: per container width tier, 2, 3 or 4 equal columns whenever every title (beside its price, when
  // the value has one) fits in at most two lines, wrapping between words; the most columns that fit with balanced
  // rows (never a lone chip on a last row); otherwise full-width rows.
  const TIERS = [272, 300, 320, 340, 360, 380, 400, 430, 460, 490, 520];
  let measure;
  const textWidth = (text, font) => {
    measure = measure || document.createElement('canvas').getContext('2d');
    measure.font = font;
    return Math.ceil(measure.measureText(text).width);
  };
  // Lines a greedy wrap between words needs at this width (99 when one word alone is wider: words never break).
  const lineCount = (words, avail, space) => {
    let lines = 1;
    let line = 0;
    for (const word of words) {
      if (word > avail) return 99;
      if (!line) line = word;
      else if (line + space + word > avail) { lines += 1; line = word; } else line += space + word;
    }
    return lines;
  };
  // r10b item 8 (rule 9 in the Liquid): a measurement, size, part number or code (a digit, a slash or a quote mark) stays
  // on one line, and only letter words wrap. A code too wide for one line even as a full-width row in the narrowest
  // column (220 px of label room at 272) may wrap between words, as before.
  const CODE = /[0-9/"\u201c\u201d\u2033]/;
  const oneLine = (label, font, price = '') => {
    if (!CODE.test(label)) return false;
    return textWidth(label.replace(/\s+/g, ' ').trim(), font) + (price ? textWidth(price, font) + 12 : 0) <= 220;
  };
  const choiceColumns = (labels, font, prices = []) => {
    const space = textWidth(' ', font);
    const items = labels.map((label, index) => ({
      words: label.split(/\s+/).filter(Boolean).map((word) => textWidth(word, font)),
      // A price sits on the title's line, pushed to the end with a 12 px gap.
      price: prices[index] ? textWidth(prices[index], font) + 12 : 0,
      lines: oneLine(label, font, prices[index]) ? 1 : 2,
    }));
    const count = labels.length;
    const columns = {};
    for (const tier of TIERS) {
      let best = 1;
      let bestScore = 3;
      for (let cols = 2; cols <= Math.min(4, count); cols += 1) {
        if (count % cols === 1) continue;
        const score = cols * 3 - (Math.ceil(count / cols) * cols - count) * 2;
        if (score < bestScore) continue;
        // The column width minus 52 px: 10 px padding and a 1 px border each side, the 18 px check and its 12 px gap.
        const avail = (tier - (cols - 1) * 10) / cols - 52;
        if (items.every((item) => lineCount(item.words, avail - item.price, space) <= item.lines)) { best = cols; bestScore = score; }
      }
      columns[`--cq-cols-${tier}`] = best;
    }
    return columns;
  };

  customElements.define('cq-purchase-options', class extends HTMLElement {
    connectedCallback() {
      if (this.cqEvents) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.variants = JSON.parse(this.querySelector('[data-cq-variants]').textContent);
      this.planGroups = JSON.parse(this.querySelector('[data-cq-plan-groups]').textContent);
      this.groups = [...this.querySelectorAll('[data-cq-option]')];
      // The option values compared on every tap, normalized once: each variant's values (variant.norm), the variants
      // holding each value (byValue, in catalogue order), each group's inputs with their values (groupInputs) and each
      // group's value order (orders, the distance rule in nearestVariant).
      this.variants.forEach((v) => { v.norm = (v.options || []).map(normalized); });
      this.byValue = new Map();
      this.variants.forEach((v) => v.norm.forEach((value, i) => {
        const key = `${i}|${value}`;
        if (!this.byValue.has(key)) this.byValue.set(key, []);
        this.byValue.get(key).push(v);
      }));
      this.groupInputs = this.groups.map((group) => [...group.querySelectorAll('input')].map((input) => { input.cqNorm = normalized(input.value); return input; }));
      this.orders = this.groupInputs.map((inputs) => inputs.map((input) => input.cqNorm));
      this.variant = this.variants.find((v) => String(v.id) === this.form.elements.id.value) || this.variants[0];
      this.moneyFormat = deriveMoneyFormat(this.dataset.moneyUnit, this.dataset.moneyGrouped);
      // Release 1 lane U: the delivery frequency chosen. The frequency select (v5: in the Subscribe & save row) shows
      // it, and the selling plan input carries it while subscribing.
      this.planChoice = this.form.elements.selling_plan.value || this.querySelector('[data-cq-plan-select]')?.value || '';
      // A plan named by the link (?selling_plan=) is the shopper's choice: followLoop never moves it.
      this.planFromLink = this.hasAttribute('data-cq-plan-link');
      // r10 item 13: the replacement cartridge offer (snippets/cq-replacement-offer.liquid), product page only.
      // r11 item 3: one card per replacement cartridge (snippets/cq-replacement-card.liquid), each with its own data.
      this.replace = this.querySelector('[data-cq-replace]');
      this.replaceCards = [];
      if (this.replace) {
        this.replaceCards = [...this.replace.querySelectorAll('[data-cq-replace-card]')].map((card) => {
          try {
            const select = card.querySelector('[data-cq-replace-plan-select]');
            const initialChoice = select?.querySelector('option[selected]')?.value || select?.options[0]?.value;
            const restored = Boolean(select?.value && initialChoice && select.value !== initialChoice);
            return { card, input: card.querySelector('[data-cq-replace-input]'), select, choice: restored ? select.value : undefined, choiceTouched: restored, data: JSON.parse(card.querySelector('[data-cq-replace-data]').textContent) };
          } catch (error) { return null; }
        }).filter((entry) => entry && entry.input);
        if (!this.replaceCards.length) this.replace = null;
      }
      this.cqEvents = new AbortController();
      const options = { signal: this.cqEvents.signal };
      // r8 item 21: the total's status line speaks only after the shopper has done something here (never at load,
      // when Bold applies a preselected add-on).
      const touched = () => { this.touched = true; };
      this.addEventListener('pointerdown', touched, { ...options, capture: true });
      this.addEventListener('keydown', touched, { ...options, capture: true });
      // The fit check (fitTotal) runs when the visible Add to cart button changes size.
      // fitTotal and fitSku measure through the shared post-paint queue (CQMeasure), each under its own key.
      this.fitKeys = { total: { fit: 'total' }, sku: { fit: 'sku' } };
      this.fitObserver = new ResizeObserver(() => this.schedulePresentation());
      this.summaryRoots().forEach((root) => this.fitObserver.observe(root));
      // v5.7: a second fit pass once the fonts arrive, only when they have not yet (an already loaded font set would
      // measure the same text again, forcing one more layout of the whole page at load).
      if (document.fonts && document.fonts.status !== 'loaded') document.fonts.ready.then(() => { if (this.isConnected) this.schedulePresentation(); });

      this.addEventListener('change', (event) => {
        if (event.target.closest('[data-cq-option]')) this.selectVariant(event.target);
        else if (event.target.matches('[data-cq-toggle-input]')) this.applyToggle(event.target);
        else if (event.target.matches('[data-cq-replace-input]')) { this.syncReplaceAll(true); this.syncCheckout(); this.renderTotal(); }
        else if (event.target.matches('[data-cq-replace-plan-select]')) this.chooseReplacePlan(event.target);
        else if (event.target.matches('[data-cq-plan-select]')) this.choosePlan(event.target);
        else if (event.target.closest('.cq-purchase-types, [data-cq-plan-wrap]')) this.update();
        else if (event.target === this.form.elements.quantity) this.syncQuantity();
        else if (event.target.closest('.bold_options')) requestAnimationFrame(() => this.renderTotal());
      }, options);

      this.addEventListener('input', (event) => {
        if (event.target === this.form.elements.quantity && event.target.checkValidity()) this.renderTotal();
        else if (event.target.closest('.bold_options')) requestAnimationFrame(() => this.renderTotal());
      }, options);

      this.addEventListener('click', (event) => {
        // Release 1 lane Y: "Use our most popular setup" picks those option values in one tap.
        const popular = event.target.closest('[data-cq-popular]');
        if (popular) { this.choosePopular(popular); return; }
        const replacementArrow = event.target.closest('[data-cq-replace-prev], [data-cq-replace-next]');
        if (replacementArrow && this.replace?.contains(replacementArrow)) {
          event.preventDefault();
          this.moveReplace(replacementArrow.hasAttribute('data-cq-replace-prev') ? -1 : 1);
          return;
        }
        // r11 item 3: "Add all" ticks every replacement card that shows; once all are ticked, "Remove all" unticks them.
        const all = event.target.closest('[data-cq-replace-all]');
        if (all) {
          const shown = this.replaceShown();
          const tick = !shown.every((entry) => entry.input.checked);
          shown.forEach((entry) => { entry.input.checked = tick; });
          this.syncReplaceAll(true);
          this.syncCheckout();
          this.renderTotal();
          return;
        }
        // r8 item 29: a collapsed add-on group's Change / Add, and a multi-select group's Done.
        const toggle = event.target.closest('[data-cq-group-toggle]');
        if (toggle) {
          const group = toggle.closest('.bold_option');
          if (group) this.toggleGroup(group, !group.hasAttribute('data-cq-open'));
          return;
        }
        const done = event.target.closest('[data-cq-group-close]');
        if (done) {
          const group = done.closest('.bold_option');
          if (group) this.toggleGroup(group, false, { focus: true });
          return;
        }
        const button = event.target.closest('[data-cq-quantity]');
        if (!button || button.disabled) return;
        const input = this.form.elements.quantity;
        if (button.dataset.cqQuantity === '1') input.stepUp(); else input.stepDown();
        this.syncQuantity();
      }, options);

      // Capture phase at the form runs before product-form's submit listener.
      this.form.addEventListener('submit', (event) => {
        // Release 1 lane C follow-up: Add to cart and Buy now send what the controls show (see resync()).
        this.resync();
        if (!this.isPurchasable() || !this.form.elements.quantity.checkValidity()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.form.elements.quantity.reportValidity();
        }
      }, { ...options, capture: true });

      this.update();
      this.setupSticky();
      // AJ 2026-10-11 (option A): Loop's live defaults (assets/cq-loop-default.js), read once per visit. They move the
      // frequency only when the theme's copy of Loop's defaults is out of date and the shopper has not touched this
      // picker yet (followLoop); normally the page already starts on Loop's default and nothing moves.
      window.CQLoopDefaults?.load().then((ids) => { if (ids && this.isConnected) this.followLoop(ids); });
      // Release 1 lane C follow-up (Back button). A page restored from the back/forward cache comes back exactly as it
      // was left. Without that cache the page loads again, and the browser may then restore the purchase radios, the
      // plan select and the other picker controls (no change event) after update() has run: Chrome does it in a task
      // after parsing, after this deferred script. Subscribe then showed checked over the one-time price, with the
      // selling plan input off, so Add to cart added no plan. The snippet marks those controls autocomplete="off" so
      // the page opens as rendered; resync() is the net for a browser that restores them anyway: on pageshow, a task
      // after it, a task after DOMContentLoaded, and before every submit (above).
      const later = () => setTimeout(() => this.resync(), 0);
      window.addEventListener('pageshow', () => { this.resync(); later(); }, options);
      if (document.readyState !== 'complete') document.addEventListener('DOMContentLoaded', later, { ...options, once: true });
      // r10 item 4: the installments widget in this picker's price block (the popup's is upgraded with this element,
      // so it is styled once the insertion's upgrades have run, before the popup paints).
      queueMicrotask(() => this.summaryRoots().forEach((root) => window.CQPurchaseTerms?.style(root)));

      // r6 item 11: Bold Product Options fills its mount after load and re-renders it on its own events.
      const bold = this.querySelector('.bold_options');
      if (bold) {
        // r8 item 21 performance: one enhanceBold() pass per frame at most. The frame drops the records our own
        // writes caused (takeRecords), and every write in the pass checks first, so the pass settles.
        this.boldObserver = new MutationObserver(() => {
          cancelAnimationFrame(this.boldFrame);
          // r8 item 29 fix: a group Bold has just drawn (not yet seen by enhanceGroups) is folded in this same
          // callback, which runs before the browser paints, so its open list is never shown and the rows under it
          // never jump. The pass a frame later let the browser paint Bold's open groups first when Bold rendered late
          // in a frame (a 0.0064 load shift on the Thunder pages at 1440). Only new groups take this path; every
          // other change keeps the one pass per frame.
          if (bold.querySelector(NEW_GROUP) || bold.querySelector(NEW_TOGGLES)) {
            this.enhanceBold();
            this.boldObserver?.takeRecords();
            return;
          }
          this.boldFrame = requestAnimationFrame(() => {
            this.enhanceBold();
            this.boldObserver?.takeRecords();
          });
        });
        this.boldObserver.observe(bold, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
        if (bold.hasAttribute('data-cq-bold-wait')) this.watchBoldAnswer(bold);
        this.enhanceBold();
        // If Bold never renders, give the reserved space back.
        this.boldRelease = setTimeout(() => this.releaseBold(bold), 8000);
      }
    }

    setupSticky() {
      if (this.form.dataset.eventContext !== 'MainProductPage') return;
      const start = () => {
        if (!this.isConnected || document.querySelector('sticky-cart')) return;
        const id = this.form.getAttribute('id');
        const template = document.querySelector(`template[data-cq-sticky-template="${CSS.escape(id)}"]`);
        if (template) {
          const fragment = template.content.cloneNode(true);
          this.stickyCart = fragment.querySelector('sticky-cart');
          document.body.append(fragment);
        }
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true, signal: this.cqEvents.signal });
      else start();
    }

    boldKey(bold) {
      return `${bold.dataset.cqBoldKey || 'cq-bold'}:${bold.dataset.productId}:${Math.round(window.innerWidth / 100)}`;
    }

    // First visit (see the snippet): Add to cart and the trust row wait, hidden in place, until Bold has
    // rendered (enhanceBold reveals them once Bold is still) or has answered that this product has no add-ons.
    // Bold fetches each product's add-ons from its generate_option endpoint; when that answer arrives and
    // nothing renders, reveal and remember "none" so this visitor's next view does not wait. If the endpoint
    // ever changes, the snippet's 3 s timer still reveals. If Bold's script did not load at all (blocked or
    // down), there is nothing to wait for: reveal as soon as the deferred scripts have run.
    watchBoldAnswer(bold) {
      const loaded = () => { if (!window.BOLD?.options?.app) this.revealBold(bold); };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', loaded, { once: true }); else loaded();
      const answer = `/generate_option/${bold.dataset.productId}`;
      try {
        this.boldAnswerObserver = new PerformanceObserver((list) => {
          const entry = list.getEntries().find((item) => item.name.includes(answer));
          if (!entry) return;
          this.boldAnswerObserver.disconnect();
          this.boldAnswerObserver = null;
          // Remember "none" only for a real answer; a failed request just reveals.
          const answered = entry.responseStatus >= 200 && entry.responseStatus < 300;
          this.boldEmptyTimer = setTimeout(() => {
            if (bold.querySelector('.bold_option')) return;
            this.revealBold(bold);
            if (!answered) return;
            try { localStorage.setItem(this.boldKey(bold), '0'); } catch (error) { /* storage unavailable */ }
          }, 300);
        });
        this.boldAnswerObserver.observe({ type: 'resource', buffered: true });
      } catch (error) { /* no Resource Timing: the snippet's timer reveals */ }
    }

    revealBold(bold) {
      clearTimeout(this.boldRevealTimer);
      ['.cq-pdp-purchase', '.product-info-wrap-content', '.cq-product-page'].map((selector) => bold.closest(selector)).concat(bold)
        .forEach((element) => element?.removeAttribute('data-cq-bold-wait'));
    }

    releaseBold(bold) {
      if (!bold.hasAttribute('data-cq-reserved')) return;
      bold.style.minHeight = '';
      bold.removeAttribute('data-cq-reserved');
    }

    disconnectedCallback() {
      this.cqEvents?.abort();
      this.cqEvents = null;
      this.presentationToken = (this.presentationToken || 0) + 1;
      this.presentationPending = false;
      this.stickyCart?.remove();
      this.stickyCart = null;
      this.boldObserver?.disconnect();
      this.boldObserver = null;
      this.boldAnswerObserver?.disconnect();
      this.boldAnswerObserver = null;
      clearTimeout(this.boldRelease);
      clearTimeout(this.boldStoreTimer);
      clearTimeout(this.boldEmptyTimer);
      clearTimeout(this.boldRevealTimer);
      cancelAnimationFrame(this.boldFrame);
      clearTimeout(this.statusTimer);
      this.groupTimers?.forEach((timer) => clearTimeout(timer));
      this.groupTimers?.clear();
      this.fitObserver?.disconnect();
      this.fitObserver = null;
      this.fitTarget = null;
      if (this.fitKeys) { window.CQMeasure?.cancel(this.fitKeys.total); window.CQMeasure?.cancel(this.fitKeys.sku); }
      if (this.boldListener) {
        this.boldEmitter?.removeListener?.('BOLD_OPTIONS_total_changed', this.boldListener);
        this.boldListener = null;
      }
    }

    enhanceBold() {
      const bold = this.querySelector('.bold_options');
      if (!bold) return;
      if (bold.querySelector('.bold_option')) {
        // r8 item 21: Bold's total change is the main trigger for the button total (the observer stays the
        // fallback). Bold does not fire it on quantity changes; syncQuantity() covers those.
        const emitter = window.BOLD?.common?.eventEmitter;
        if (!this.boldListener && typeof emitter?.on === 'function') {
          this.boldEmitter = emitter;
          this.boldListener = () => this.renderTotal();
          emitter.on('BOLD_OPTIONS_total_changed', this.boldListener);
        }
        this.releaseBold(bold);
        // First visit: reveal what waited below once Bold has been still for a moment (it renders in steps).
        if (bold.hasAttribute('data-cq-bold-wait')) {
          clearTimeout(this.boldRevealTimer);
          this.boldRevealTimer = setTimeout(() => this.revealBold(bold), 150);
        }
        // Remember the rendered height for this visitor's next view (see the snippet's reservation), once Bold
        // has been still for a moment so a render in several steps is measured complete.
        if (!this.boldStored) {
          clearTimeout(this.boldStoreTimer);
          this.boldStoreTimer = setTimeout(() => {
            this.boldStored = true;
            try { localStorage.setItem(this.boldKey(bold), String(Math.round(bold.getBoundingClientRect().height))); } catch (error) { /* storage unavailable */ }
          }, 700);
        }
      }
      // Bold marks the chosen image swatch with a class but leaves aria-checked="false"; keep them in step.
      // r8 item 22: the tile's name is the add-on and its price ("Bath Ball Hook, add $2.50"; Bold's says "color option:
      // ... with an additional charge of"); a "No ..." value (no product behind it) is its name alone. Set only when it
      // differs (the observer does not watch aria-label).
      const valueLabel = this.dataset.addonValue || '';
      bold.querySelectorAll('.bold_option_value_element[role="checkbox"]').forEach((swatch) => {
        const checked = String(swatch.classList.contains('bold_swatch_selected'));
        if (swatch.getAttribute('aria-checked') !== checked) swatch.setAttribute('aria-checked', checked);
        const label = swatch.closest('label');
        const name = label?.querySelector('.bold_option_value_title')?.textContent.replace(/\s+/g, ' ').trim();
        if (!name) return;
        const price = label.querySelector('.bold_option_value_price')?.textContent.replace(/\s+/g, ' ').trim();
        const real = Boolean(swatch.querySelector('input[data-product-handle]')?.dataset.productHandle);
        const text = real && price && valueLabel ? valueLabel.replace('__NAME__', name).replace('__PRICE__', price) : name;
        if (swatch.getAttribute('aria-label') !== text) swatch.setAttribute('aria-label', text);
      });
      // A display text with a blank title ("&nbsp;") is a heading for the add-ons under it, not a body note.
      bold.querySelectorAll('.bold_option > .bold_option_title').forEach((title) => {
        const blank = !title.textContent.replace(/\u00a0/g, ' ').trim();
        title.classList.toggle('cq-bold-blank', blank);
        const option = title.parentElement;
        if (option.classList.contains('bold_option_displaytext')) option.classList.toggle('cq-bold-heading', blank);
      });
      // v5.2: the options app's "Optional Additions and Upgrades" heading reads "Upgrades (optional)" (the theme's
      // string, data-bold-heading-upgrades), and an optional dropdown title ("Oxidation System") says so. Presentation
      // only: the app's option names, property keys and prices are untouched, and only differing text is written.
      const upgrades = this.dataset.boldHeadingUpgrades;
      if (upgrades) {
        bold.querySelectorAll('.bold_option_displaytext.cq-bold-heading .bold_option_element span').forEach((text) => {
          if (/^\s*optional additions and upgrades\s*:?\s*$/i.test(text.textContent)) setText(text, upgrades);
        });
      }
      const optional = this.dataset.boldOptional;
      if (optional) {
        const model = this.boldModel();
        bold.querySelectorAll('.bold_option_dropdown > label > .bold_option_title').forEach((title) => {
          const group = title.closest('.bold_option');
          const option = model.get(group);
          const text = cleanText(title);
          const required = option ? Boolean(option.isRequired) : /\*\s*$/.test(text);
          if (required || !text || new RegExp(optional.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '$', 'i').test(text) || /optional/i.test(text)) return;
          setText(title, `${text} ${optional}`);
        });
      }
      // r8 item 24: Bold's group titles lose a trailing colon ("Include Bath Ball Hook:" reads "Include Bath Ball
      // Hook"), and so does a display text that serves as a heading ("Optional Additions and Upgrades:"); a required
      // "*" stays. Only the last text node is edited, and only when it ends with ":", so the pass settles (the
      // observer does not watch text, and the frame drops our own records). The hidden input names, and so the
      // cart's property keys, keep their colon.
      bold.querySelectorAll('.bold_option_title, .bold_option_displaytext.cq-bold-heading .bold_option_element').forEach((title) => {
        if (!TITLE_COLON.test(title.textContent)) return;
        const walker = document.createTreeWalker(title, NodeFilter.SHOW_TEXT);
        let last = null;
        for (let node = walker.nextNode(); node; node = walker.nextNode()) {
          const text = node.data.trim();
          if (text && text !== '*') last = node;
        }
        if (last && TITLE_COLON.test(last.data)) last.data = last.data.replace(TITLE_COLON, '$1');
      });
      bold.querySelectorAll('.bold_option_radio .bold_option_element').forEach((list) => {
        const titles = [...list.querySelectorAll('.bold_option_value_title')];
        if (!titles.length) return;
        const labels = titles.map((title) => title.textContent.replace(/\s+/g, ' ').trim());
        // Bold prints the price without its "+" (CSS adds it), so the "+" is measured with it.
        const prices = titles.map((title) => {
          const price = title.closest('label')?.querySelector('.bold_option_value_price')?.textContent.replace(/\s+/g, ' ').trim();
          return price ? `+${price}` : '';
        });
        const style = getComputedStyle(titles[0]);
        const font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        // r10b item 8: a code value keeps one line (written only when it differs, so the pass settles).
        titles.forEach((title, index) => {
          const line = oneLine(labels[index], font, prices[index]);
          if (title.hasAttribute('data-cq-line') !== line) title.toggleAttribute('data-cq-line', line);
        });
        const key = `${labels.join('|')}|${prices.join('|')}`;
        if (list.dataset.cqLayout === key) return;
        list.dataset.cqLayout = key;
        const columns = choiceColumns(labels, font, prices);
        Object.entries(columns).forEach(([name, value]) => list.style.setProperty(name, value));
      });
      this.overrideHelp(bold);
      this.enhanceToggles(bold);
      this.enhanceGroups(bold);
      // r8 item 21: Bold's visible "Total Extras" line is hidden by CSS (its element and _sc* inputs stay: they put
      // the add-ons in the cart). Round 4 (2026-10-15): no "Includes $X in add-ons" note takes its place any more;
      // each add-on card shows its own price and Add to cart shows the total.
      this.renderTotal();
    }

    // r10 item 10 (AJ 2026-10-09: "the callout for the whole house ro is not if they want highly alkaline water its
    // something like if they want to raise the tds after purification"). Bold's help text for a group lives in Bold's
    // own data (the option's help text, shown under the group). The theme replaces what it displays for an option named
    // in data-bold-help (snippets/cq-option-picker.liquid: Bold's option name to a cq_pdp.help_* text in the locale
    // files). Only the shown text changes; the app keeps its own, and an edit there is still overridden here until the
    // entry is removed. Written only when it differs, so the pass settles.
    overrideHelp(bold) {
      if (this.helpOverrides === undefined) {
        this.helpOverrides = new Map();
        try {
          Object.entries(JSON.parse(this.dataset.boldHelp || '{}')).forEach(([name, text]) => {
            if (text) this.helpOverrides.set(name.trim().toLowerCase(), text);
          });
        } catch (error) { /* no overrides */ }
      }
      if (!this.helpOverrides.size) return;
      const model = this.boldModel();
      bold.querySelectorAll('.bold_option').forEach((group) => {
        const help = group.querySelector('.bold_help_text');
        if (!help) return;
        const title = cleanText(group.querySelector(':scope > .bold_option_title, :scope > label > .bold_option_title')).replace(/\s*\*$/, '');
        const name = (model.get(group)?.publicName || title).trim().toLowerCase();
        const text = this.helpOverrides.get(name);
        if (text) setText(help.querySelector('small') || help, text);
      });
    }

    // r10 item 9 (AJ 2026-10-09: "instead of having uv and remin seperate can we put add post filtration or something
    // and then have no remineralizer hidden if selected and then only show the uv and the alkalizing"). Presentation
    // only: Bold's option data, prices and cart properties stay exactly as they were.
    // - A Yes / No pair (an optional single-choice group of two values, one a real add-on and the other a "No ..."
    //   value with no price and no product) becomes one toggle card: checked picks the add-on, unchecked picks the
    //   "No ..." value behind the scenes, so that value never shows and the product can still be bought without it.
    // - A run of such pairs and optional single check box add-ons, all of them post-filters (UV sterilizer,
    //   remineralizer, alkalizer), shares one heading, "Add post-filtration (optional)". A pair outside such a run
    //   keeps Bold's own group title as its heading. Census of Bold's data for all 527 published products
    //   (2026-10-09): the only pairs are the remineralizer groups of the whole house, low flow and mid flow RO systems,
    //   each after a 12 GPM UV check box.
    // - Each card is a real check box (its name says the add-on and its price) in one fieldset. Bold's own groups stay
    //   in the form, hidden (their inputs still carry the add-ons to the cart); a card drives them through Bold's own
    //   click handlers, and every pass reads Bold's state back into the cards.
    enhanceToggles(bold) {
      const model = this.boldModel();
      bold.querySelectorAll('.bold_option_set').forEach((set) => {
        if (!set.hasAttribute('data-cq-toggle-set')) set.setAttribute('data-cq-toggle-set', '');
        const groups = [...set.children].filter((child) => child.classList.contains('bold_option'));
        const runs = [];
        let run = [];
        groups.forEach((group) => {
          if (!group.hasAttribute('data-cq-toggle-seen')) group.setAttribute('data-cq-toggle-seen', '');
          const item = group.classList.contains('bold_option_hidden') ? null : this.readToggle(group, model);
          if (item) { run.push(item); return; }
          if (run.length) runs.push(run);
          run = [];
        });
        if (run.length) runs.push(run);
        const wanted = [];
        runs.forEach((items) => {
          if (!items.some((item) => item.kind === 'pair')) return;
          if (items.every((item) => POST_FILTER.test(item.name))) wanted.push({ heading: this.dataset.postFiltration || '', items });
          else items.filter((item) => item.kind === 'pair').forEach((item) => wanted.push({ heading: item.title, items: [item] }));
        });
        const blocks = [...set.querySelectorAll(':scope > [data-cq-toggles]')];
        wanted.forEach((want, index) => {
          const key = want.items.map((item) => `${item.kind}:${item.name}`).join('|');
          let block = blocks[index];
          if (!block || block.dataset.cqToggles !== key || block.nextElementSibling !== want.items[0].group) {
            block?.remove();
            block = this.buildToggles(want, key);
            want.items[0].group.before(block);
          }
          this.syncToggles(block, want);
        });
        blocks.slice(wanted.length).forEach((block) => block.remove());
        const folded = new Set(wanted.flatMap((want) => want.items.map((item) => item.group)));
        groups.forEach((group) => {
          if (group.hasAttribute('data-cq-toggled') !== folded.has(group)) group.toggleAttribute('data-cq-toggled', folded.has(group));
        });
      });
    }

    // One add-on a toggle card can stand for, or null: an optional single check box with a product behind it
    // ('check'), or an optional Yes / No pair ('pair').
    readToggle(group, model) {
      const option = model.get(group);
      const title = cleanText(group.querySelector(':scope > .bold_option_title, :scope > label > .bold_option_title'));
      const required = option ? Boolean(option.isRequired) : /\*\s*$/.test(title);
      if (required) return null;
      const price = (scope) => cleanText(scope.querySelector('.bold_option_value_price'));
      const help = cleanText(group.querySelector('.bold_help_text'));
      if (group.classList.contains('bold_option_checkbox')) {
        const boxes = group.querySelectorAll('input[type="checkbox"]');
        if (boxes.length !== 1 || !boxes[0].dataset.productHandle) return null;
        return {
          kind: 'check', group, title, name: title.replace(OPTIONAL_SUFFIX, ''), price: price(group), help,
          optionName: option?.publicName?.trim() || title, control: boxes[0], handle: boxes[0].dataset.productHandle, photo: '',
        };
      }
      if (!group.matches('.bold_option_swatch, .bold_option_radio')) return null;
      const values = [...group.querySelectorAll('.bold_option_value')];
      if (values.length !== 2) return null;
      const handleOf = (value) => value.querySelector('input[data-product-handle]')?.dataset.productHandle || '';
      const real = values.find((value) => handleOf(value));
      const none = values.find((value) => !handleOf(value));
      if (!real || !none || price(none) || !NONE_VALUE.test(cleanText(none.querySelector('.bold_option_value_title')))) return null;
      const swatch = real.querySelector('.bold_option_value_swatch > span')?.style.backgroundImage || '';
      return {
        kind: 'pair', group, title, name: cleanText(real.querySelector('.bold_option_value_title')), price: price(real), help,
        optionName: option?.publicName?.trim() || title, real, none, handle: handleOf(real),
        photo: /^url\(["']?(.*?)["']?\)$/.exec(swatch)?.[1] || '',
      };
    }

    buildToggles(want, key) {
      this.toggleCount = (this.toggleCount || 0) + 1;
      const base = `${this.form.getAttribute('id') || 'cq-purchase'}-toggles${this.toggleCount}`;
      const block = document.createElement('fieldset');
      block.className = 'cq-bold-toggles';
      block.setAttribute('data-cq-toggles', key);
      const legend = document.createElement('legend');
      legend.className = 'cq-purchase-legend';
      legend.textContent = want.heading;
      const list = document.createElement('div');
      list.className = 'cq-bold-toggles-list';
      want.items.forEach((item, index) => {
        const entry = document.createElement('div');
        entry.className = 'cq-bold-toggle-item';
        entry.innerHTML = `<label class="cq-bold-toggle" data-cq-toggle-card="${index}">`
          + '<span class="cq-bold-toggle-tile" aria-hidden="true"></span>'
          + '<span class="cq-bold-toggle-name" aria-hidden="true"></span>'
          + '<span class="cq-bold-toggle-price" aria-hidden="true"></span>'
          + `<input type="checkbox" class="cq-bold-toggle-input" data-cq-toggle-input data-cq-help="${base}-help${index}">`
          + `</label><p class="cq-bold-toggle-help" id="${base}-help${index}" hidden></p>`;
        list.append(entry);
      });
      block.append(legend, list);
      // Release 1 lane O: the post-filtration "How to choose" guide (snippets/cq-option-guide.liquid) beside this heading.
      if (want.heading && want.heading === this.dataset.postFiltration) {
        this.postGuide = this.postGuide || this.querySelector('cq-option-guide[data-cq-guide-dock]');
        if (this.postGuide) { legend.after(this.postGuide); this.postGuide.hidden = false; }
      }
      return block;
    }

    // Bold's state, names and prices into the cards. Only what differs is written (checked is a property, which the
    // observer does not see), so the pass settles.
    syncToggles(block, want) {
      const entries = block.querySelectorAll('.cq-bold-toggle-item');
      want.items.forEach((item, index) => {
        const entry = entries[index];
        if (!entry) return;
        const input = entry.querySelector('[data-cq-toggle-input]');
        input.cqToggle = item;
        const on = item.kind === 'check' ? item.control.checked : this.valueChosen(item.real);
        if (input.checked !== on) input.checked = on;
        // The visible name breaks after a slash between words ("Alkalizer/ Remineralizer"); the input's name is clean.
        setText(entry.querySelector('.cq-bold-toggle-name'), item.name.replace(SLASH_G, '$1/​'));
        setText(entry.querySelector('.cq-bold-toggle-price'), item.price ? `+${item.price}` : '');
        const label = item.price && this.dataset.addonValue
          ? this.dataset.addonValue.replace('__NAME__', item.name).replace('__PRICE__', item.price) : item.name;
        if (input.getAttribute('aria-label') !== label) input.setAttribute('aria-label', label);
        const help = entry.querySelector('.cq-bold-toggle-help');
        setText(help, item.help);
        if (help.hidden === Boolean(item.help)) help.hidden = !item.help;
        const described = item.help ? input.dataset.cqHelp : null;
        if (input.getAttribute('aria-describedby') !== described) {
          if (described) input.setAttribute('aria-describedby', described); else input.removeAttribute('aria-describedby');
        }
        const tile = entry.querySelector('.cq-bold-toggle-tile');
        const photo = item.photo ? Promise.resolve(item.photo) : (item.handle ? togglePhoto(item.handle) : Promise.resolve(''));
        if (tile.dataset.cqFor === item.handle) return;
        tile.dataset.cqFor = item.handle;
        photo.then((src) => {
          if (!src || tile.dataset.cqFor !== item.handle || tile.querySelector('img')) return;
          const image = document.createElement('img');
          image.alt = '';
          image.decoding = 'async';
          image.width = 56;
          image.height = 56;
          image.src = src;
          tile.append(image);
        });
      });
      // V0 #12: a Bold swatch or radio add-on is marked here, after the guide's own frame; an open guide reads it now.
      this.querySelectorAll('cq-option-guide').forEach((guide) => { if (guide.isOpen?.()) guide.sync?.(); });
    }

    // A swatch value is chosen when Bold marks its tile; a radio value when its radio is checked.
    valueChosen(value) {
      if (value.closest('.bold_option_swatch')) return Boolean(value.querySelector('.bold_swatch_selected'));
      return Boolean(value.querySelector('input[type="radio"]:checked'));
    }

    // A card was ticked or unticked: Bold's own control is clicked (Bold's handlers update its inputs, the total and
    // the cart properties), then the cards read Bold back, so a card never claims a choice Bold did not take.
    applyToggle(input) {
      const item = input.cqToggle;
      if (!item?.group.isConnected) return;
      if (item.kind === 'check') {
        if (item.control.checked !== input.checked) item.control.click();
      } else {
        const value = input.checked ? item.real : item.none;
        if (!this.valueChosen(value)) {
          (value.closest('.bold_option_swatch') ? value.querySelector('.bold_option_value_element') : value.querySelector('input[type="radio"]'))?.click();
        }
      }
      const bold = this.querySelector('.bold_options');
      if (bold) this.enhanceToggles(bold);
    }

    // r8 item 29: Bold's own option model, keyed by each option's element (required flag, type, value prices).
    boldModel() {
      const map = new Map();
      try {
        const product = window.BOLD?.options?.app?.getOptionProductByForm?.(this.form);
        product?.optionSets?.forEach((set) => set.options?.forEach((option) => {
          if (option?.DOM?.element) map.set(option.DOM.element, option);
        }));
      } catch (error) { /* no model: the rendered options are read instead */ }
      return map;
    }

    // r8 item 29: long choice groups (addendum item 29). Bold's nodes stay where Bold put them: this pass only adds
    // its own nodes (a two-line name next to a bracketed value title, one summary row per collapsible group, a Done
    // row for multi-select groups), sets attributes, and writes text only when it differs. No layout reads.
    // - Required groups (Bold's model, else Bold's "*" marker) are never collapsed.
    // - Optional multi-select groups (pre-filters) start collapsed: "None added" / "2 added · +$128.00", then Add or
    //   Change; they stay open while ticking and close on Done.
    // - Single-choice groups with more than COLLAPSE_AFTER values (the Thunder faucets) start collapsed: the chosen
    //   photo and name, then Change; choosing closes the grid and puts focus on Change.
    enhanceGroups(bold) {
      const model = this.boldModel();
      bold.querySelectorAll('.bold_option').forEach((group) => {
        const grid = group.querySelector(':scope > .bold_option_element');
        if (!grid) return;
        if (group.classList.contains('bold_option_swatch')) this.splitNames(grid);
        const option = model.get(group);
        let mode = group.getAttribute('data-cq-collapse');
        if (!mode) {
          const type = String(option?.optionData?.type || option?.type || '');
          const title = cleanText(group.querySelector(':scope > .bold_option_title'));
          const required = option ? Boolean(option.isRequired) : /\*$/.test(title);
          const multi = MULTI_TYPES.test(type) || group.classList.contains('bold_option_checkboxmulti');
          const single = !multi && group.matches('.bold_option_swatch, .bold_option_radio');
          const count = grid.querySelectorAll('.bold_option_value').length;
          // Release 1 lane Y: the faucet group keeps its summary row (where "Faucet Style" and the no-faucet line live)
          // even if Bold ever lists fewer faucets.
          group.cqFaucet = single && FAUCET_STYLE.test(title.replace(/\s*\*$/, '')) && Boolean(this.dataset.faucetNone);
          mode = 'off';
          if (!required && multi) mode = 'multi';
          else if (!required && single && (count > COLLAPSE_AFTER || group.cqFaucet)) mode = 'single';
          group.setAttribute('data-cq-collapse', mode);
          group.cqRequired = required;
        }
        if (mode === 'off') return;
        let summary = group.querySelector(':scope > [data-cq-group-summary]');
        if (!summary) summary = this.buildSummary(group, grid, mode, option);
        const open = group.hasAttribute('data-cq-open');
        const done = group.querySelector(':scope > [data-cq-group-done]');
        // Collapsed: hidden and inert (Tab and screen readers skip it). Re-applied when Bold re-renders the list;
        // left alone while the height animation runs.
        if (!group.hasAttribute('data-cq-anim')) {
          if (grid.hidden === open) grid.hidden = !open;
          if (grid.inert === open) grid.inert = !open;
          if (done && done.hidden === open) done.hidden = !open;
        }
        const values = [...grid.querySelectorAll('.bold_option_value')];
        const chosen = values.filter((value) => value.querySelector('.bold_swatch_selected, input:checked:not([type="hidden"])'));
        const toggle = summary.querySelector('[data-cq-group-toggle]');
        setText(toggle, chosen.length ? this.dataset.groupChange : this.dataset.groupAdd);
        const valueBox = summary.querySelector('.cq-bold-summary-value');
        if (mode === 'single') {
          const pick = chosen[0];
          const pickName = pick ? cleanText(pick.querySelector('.bold_option_value_title')) : '';
          // Release 1 lane Y: no faucet chosen (nothing ticked, or a "No ..." value) says what that means.
          const noFaucet = group.cqFaucet && (!pick || NONE_VALUE.test(pickName));
          if (group.cqFaucet) setText(summary.querySelector('.cq-bold-summary-name'), this.dataset.faucetHeading);
          this.writeName(valueBox, noFaucet ? this.dataset.faucetNone : (pick ? pickName : this.dataset.groupNone), !noFaucet);
          const tile = summary.querySelector('.cq-bold-summary-tile');
          const photo = noFaucet ? '' : pick?.querySelector('.bold_option_value_swatch > span')?.style.backgroundImage || '';
          const real = Boolean(pick?.querySelector('input[data-product-handle]')?.dataset.productHandle);
          if (tile) {
            const image = tile.firstElementChild;
            const shown = real ? photo : '';
            if (image.style.backgroundImage !== shown) image.style.backgroundImage = shown;
            if (tile.classList.contains('is-empty') === Boolean(shown)) tile.classList.toggle('is-empty', !shown);
          }
          // R4 finish: the chosen value's add-on price on the card's right, "+$29.95" like the add-on cards (Bold's own
          // price text for that value, the one its grid tile shows); nothing for a free choice. Without it, a faucet
          // Bold preselects raised Add to cart with nothing on screen saying why.
          const priceBox = summary.querySelector('.cq-bold-summary-price');
          if (priceBox) {
            const cost = noFaucet || !pick ? '' : cleanText(pick.querySelector('.bold_option_value_price')).replace(/^\+\s*/, '');
            const shownPrice = /[1-9]/.test(cost) ? `+${cost}` : '';
            setText(priceBox, shownPrice);
            if (priceBox.hidden === Boolean(shownPrice)) priceBox.hidden = !shownPrice;
          }
        } else {
          const amount = this.groupAmount(option, values, chosen);
          const text = chosen.length
            ? this.dataset.groupAdded.replace('__COUNT__', chosen.length).replace('__AMOUNT__', this.money(amount))
            : this.dataset.groupNone;
          this.writeName(valueBox, text, false);
        }
        // A new choice in an open single-choice group closes it (after a beat, so the check is seen), with focus
        // moved to Change when it was inside the grid. A Bold error (failed submit) opens the group.
        const key = chosen.map((value) => cleanText(value.querySelector('.bold_option_value_title'))).join('|');
        const previous = group.getAttribute('data-cq-choice');
        if (previous !== key) group.setAttribute('data-cq-choice', key);
        if (mode === 'single' && open && previous !== null && previous !== key) this.queueGroup(group, false, 150);
        if (!open && group.classList.contains('bold_option_error')) this.queueGroup(group, true, 0);
      });
    }

    // Writes a value name, its trailing bracketed part as a muted second line (the 03af46d rule).
    writeName(box, text, split = true) {
      if (!box) return;
      const match = split ? BRACKET.exec(text) : null;
      const main = match ? match[1] : text;
      const sub = match ? match[2] : '';
      // The space between the two lines keeps the words apart in the Change button's name (aria-labelledby).
      if (box.childElementCount !== 2) box.innerHTML = '<span></span> <span class="cq-bold-summary-sub"></span>';
      setText(box.firstElementChild, main);
      setText(box.lastElementChild, sub);
      if (box.lastElementChild.hidden === Boolean(sub)) box.lastElementChild.hidden = !sub;
    }

    // Two-line copies of bracketed value titles; Bold's title stays (hidden by CSS) so nothing of Bold's changes.
    // A slash-joined name ("500-2500 GPD Single Alkalizer/Remineralizer") gets a copy too, with a break chance after
    // each slash, so a narrow card wraps it at the slash instead of inside a word (the copy is aria-hidden).
    splitNames(grid) {
      grid.querySelectorAll('.bold_option_value_title').forEach((title) => {
        const text = cleanText(title);
        const match = BRACKET.exec(text);
        let copy = title.nextElementSibling?.classList.contains('cq-bold-name') ? title.nextElementSibling : null;
        if (!match && !SLASH.test(text)) { copy?.remove(); return; }
        if (!copy) {
          copy = document.createElement('span');
          copy.className = 'cq-bold-name';
          copy.setAttribute('aria-hidden', 'true');
          copy.innerHTML = '<span class="cq-bold-name-main"></span> <span class="cq-bold-name-sub"></span>';
          title.after(copy);
        }
        setText(copy.firstElementChild, (match ? match[1] : text).replace(SLASH_G, '$1/\u200B'));
        setText(copy.lastElementChild, match ? match[2] : '');
      });
    }

    buildSummary(group, grid, mode, option) {
      this.groupCount = (this.groupCount || 0) + 1;
      const base = `${this.form.getAttribute('id') || 'cq-purchase'}-group${this.groupCount}`;
      if (!grid.id) grid.id = `${base}-list`;
      const summary = document.createElement('div');
      summary.className = 'cq-bold-summary';
      summary.setAttribute('data-cq-group-summary', mode);
      // R4 finish: a single-choice row carries the chosen value's price before its button (enhanceGroups fills it);
      // a multi-select row already says its amount in the value line ("2 selected · +$128.00").
      const single = mode === 'single';
      summary.innerHTML = `${single ? '<span class="cq-bold-summary-tile is-empty" aria-hidden="true"><span></span></span>' : ''}`
        + `<span class="cq-bold-summary-text"><span class="cq-bold-summary-name" id="${base}-name"></span>`
        + `<span class="cq-bold-summary-value" id="${base}-value"></span></span>`
        + `${single ? `<span class="cq-bold-summary-price" id="${base}-price" hidden></span>` : ''}`
        + `<button type="button" class="button cq-btn-ghost cq-bold-summary-toggle" id="${base}-toggle" data-cq-group-toggle`
        + ` aria-expanded="false" aria-controls="${grid.id}" aria-labelledby="${base}-toggle ${base}-name ${base}-value${single ? ` ${base}-price` : ''}"></button>`;
      // The group's name is Bold's title without the required marker; "(optional)" only where Bold marks the group
      // as not required and the name does not already say so (Thunder: "Additional Cartridges (Optional)").
      let name = cleanText(group.querySelector(':scope > .bold_option_title')).replace(/\s*\*$/, '') || option?.publicName?.trim() || '';
      if (group.cqFaucet && this.dataset.faucetHeading) name = this.dataset.faucetHeading;
      if (mode === 'multi' && !group.cqRequired && !/optional/i.test(name) && this.dataset.groupOptional) {
        name = this.dataset.groupOptional.replace('__NAME__', name);
      }
      summary.querySelector('.cq-bold-summary-name').textContent = name;
      grid.before(summary);
      if (mode === 'multi' && !group.querySelector(':scope > [data-cq-group-done]')) {
        const row = document.createElement('div');
        row.className = 'cq-bold-done-row';
        row.setAttribute('data-cq-group-done', '');
        row.hidden = true;
        row.innerHTML = '<button type="button" class="button button-secondary cq-bold-done" data-cq-group-close></button>';
        row.firstElementChild.textContent = this.dataset.groupDone;
        grid.after(row);
      }
      return summary;
    }

    // The chosen values' add-on prices in cents, from Bold's model (same order as the rendered values); else from
    // the prices Bold submits, matched by name.
    groupAmount(option, values, chosen) {
      const prices = chosen.map((value) => Number(option?.optionValues?.[values.indexOf(value)]?.price));
      if (prices.length && prices.every(Number.isFinite)) return prices.reduce((sum, cents) => sum + cents, 0);
      const field = (name) => this.form.querySelector(`[name="properties[${name}]"]`)?.value || '';
      const names = field('_scVariantNames');
      const cents = field('_scVariantPrices').split(',');
      return chosen.reduce((sum, value) => {
        const title = cleanText(value.querySelector('.bold_option_value_title'));
        const index = names.split(',').findIndex((entry) => entry.trim() === title);
        return sum + (index >= 0 ? Number(cents[index]) || 0 : 0);
      }, 0);
    }

    queueGroup(group, open, delay) {
      this.groupTimers = this.groupTimers || new Map();
      clearTimeout(this.groupTimers.get(group));
      this.groupTimers.set(group, setTimeout(() => {
        this.groupTimers.delete(group);
        if (!group.isConnected) return;
        const focus = !open && group.querySelector(':scope > .bold_option_element')?.contains(document.activeElement);
        this.toggleGroup(group, open, { focus });
      }, delay));
    }

    // Opens or closes a collapsible group: the group's height animates over about 260 ms on --cq-ease (instant with
    // reduced motion); a closing list stays visible but inert during the animation and is hidden at its end.
    toggleGroup(group, open, { focus = false } = {}) {
      const grid = group.querySelector(':scope > .bold_option_element');
      const toggle = group.querySelector(':scope > [data-cq-group-summary] [data-cq-group-toggle]');
      if (!grid || !toggle) return;
      const done = group.querySelector(':scope > [data-cq-group-done]');
      // Measured before a running animation is cancelled, so a second toggle mid-way starts from where it is.
      const from = group.getBoundingClientRect().height;
      group.cqAnimation?.cancel();
      group.toggleAttribute('data-cq-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      grid.inert = !open;
      grid.hidden = !open;
      if (done) done.hidden = !open;
      const to = group.getBoundingClientRect().height;
      if (!open) {
        // Keep the closing list on screen while the height shrinks over it.
        grid.hidden = false;
        if (done) done.hidden = false;
      }
      if (focus) toggle.focus({ preventScroll: true });
      const finish = () => {
        group.cqAnimation = null;
        group.removeAttribute('data-cq-anim');
        group.style.removeProperty('overflow');
        const isOpen = group.hasAttribute('data-cq-open');
        grid.hidden = !isOpen;
        grid.inert = !isOpen;
        if (done) done.hidden = !isOpen;
      };
      if (matchMedia('(prefers-reduced-motion: reduce)').matches || Math.abs(from - to) < 1) { finish(); return; }
      group.setAttribute('data-cq-anim', '');
      group.style.overflow = 'clip';
      const easing = getComputedStyle(group).getPropertyValue('--cq-ease').trim() || 'cubic-bezier(.22, .61, .36, 1)';
      const animation = group.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: 260, easing });
      group.cqAnimation = animation;
      animation.onfinish = finish;
      animation.oncancel = () => { if (group.cqAnimation === animation) finish(); };
    }

    // SC Product Options exposes cents on its per-form handler or its submission fields.
    // Positive one-time amounts cannot safely be multiplied by the product quantity.
    boldPricing() {
      const bold = this.querySelector('.bold_options');
      if (!bold) return { cents: 0, unknown: false };
      const result = (cents) => this.hasOneTimeAddons() && cents > 0
        ? { cents: 0, unknown: true } : { cents, unknown: false };
      try {
        const raw = window.BOLD?.options?.app?.getOptionProductByForm?.(this.form)?.priceHandler?.total;
        const total = Number(raw);
        if (raw !== null && raw !== undefined && String(raw).trim() !== '' && Number.isFinite(total) && total >= 0) return result(Math.round(total));
      } catch (error) { /* use the app's submitted cents below */ }
      const field = (name) => this.form.querySelector(`[name="properties[${name}]"]`)?.value.trim();
      const prices = field('_scVariantPrices');
      const quantities = field('_scVariantQtys');
      if (prices) {
        const values = prices.split(',').map((value) => value.trim());
        const counts = quantities ? quantities.split(',').map((value) => value.trim()) : null;
        if (values.every((value) => /^\d+$/.test(value) && Number.isSafeInteger(Number(value)))
          && (!counts || (counts.length === values.length && counts.every((value) => /^[1-9]\d*$/.test(value))))) {
          const cents = values.reduce((sum, value, index) => sum + Number(value) * (counts ? Number(counts[index]) : 1), 0);
          if (Number.isSafeInteger(cents)) return result(cents);
        }
        return { cents: 0, unknown: true };
      }
      const model = this.boldModel();
      const selected = [...bold.querySelectorAll('input:checked, .bold_swatch_selected, select, textarea, input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"])')]
        .filter((input) => !input.disabled && (input.matches('.bold_swatch_selected') || input.value.trim()))
        .some((input) => {
          const group = input.closest('.bold_option');
          const choices = input.matches('select') ? [...input.selectedOptions] : [input];
          return choices.some((choice) => {
            const value = choice.closest('.bold_option_value');
            const values = [...(group?.querySelectorAll('.bold_option_value') || [])];
            const option = model.get(group);
            const index = choice.matches('option') ? choice.index : values.indexOf(value);
            const labelElement = choice.closest('label') || choice.labels?.[0];
            const scopes = [choice, value, labelElement].filter(Boolean);
            const prices = [option?.optionValues?.[index]?.price,
              ...scopes.flatMap((scope) => [scope.dataset.price, scope.dataset.optionPrice])];
            if (prices.some((price) => Number.isFinite(Number(price)) && Number(price) !== 0)) return true;
            // Labels only establish that a charge exists; never calculate money from display text.
            const labelScope = choice.matches('option') ? choice : value || labelElement;
            const label = labelScope?.querySelector('.bold_option_value_price')?.textContent || '';
            if (/[1-9]/.test(label)) return true;
            return choice.matches('option') && /(?:[$€£¥₹₩]|\b[A-Z]{3}\s)[\s\d.,]*[1-9]/.test(choice.textContent);
          });
        });
      return { cents: 0, unknown: selected || this.hasOneTimeAddons() };
    }

    hasOneTimeAddons() {
      return ONE_TIME.some((name) => Boolean(this.form.querySelector(`[name="properties[${name}]"]`)?.value.trim()));
    }

    money(cents) {
      return formatMoney(cents, this.moneyFormat);
    }

    // The visible Add to cart: Bold's clone once it exists (the original is then hidden), else the original.
    observeFit() {
      const target = this.querySelector('.cq-purchase-add.bold_clone') || this.querySelector('.cq-purchase-add');
      if (!target || target === this.fitTarget || !this.fitObserver) return;
      if (this.fitTarget) this.fitObserver.unobserve(this.fitTarget);
      this.fitTarget = target;
      this.fitObserver.observe(target);
    }

    // r8 item 21: total = (plan price while subscribing, else the variant price, plus Bold's add-ons) x quantity,
    // written into every Add to cart (Bold's clone included). Text is written only when it differs.
    renderTotal({ baseline = false } = {}) {
      const variant = this.variant;
      if (!variant || !this.form) return;
      const plan = this.currentPlan();
      const quantity = Math.max(1, Number(this.form.elements.quantity.value) || 1);
      const options = this.boldPricing();
      const extras = options.cents;
      const plain = !variant.available || Boolean(variant.callForPrice);
      const show = !plain;
      // r10 item 13: a ticked replacement subscription is one cartridge on its plan price, whatever the quantity (r11
      // item 3: every ticked card).
      const replace = this.replaceCents();
      const totalText = this.money(((plan ? plan.price : variant.price) + extras) * quantity + replace);
      const addonText = extras > 0 && !options.unknown ? this.money(extras * quantity) : '';
      const note = options.unknown ? this.dataset.plusSelectedOptions : '';
      this.querySelectorAll('[data-cq-options-note]').forEach((slot) => setText(slot, note || ''));
      this.observeFit();
      this.querySelectorAll('.cq-purchase-add').forEach((button) => {
        if (button.classList.contains('is-plain') !== plain) button.classList.toggle('is-plain', plain);
      });
      this.querySelectorAll('[data-cq-add-total]').forEach((wrap) => {
        if (wrap.hidden === show) wrap.hidden = !show;
        setText(wrap.querySelector('[data-cq-add-total-value]'), totalText);
      });
      // Measure only when the characters that set the width changed (or the button resized: fitObserver).
      const label = this.fitTarget?.querySelector('[data-cq-submit-label]')?.textContent || '';
      const fitKey = show ? `${label.length}|${totalText.length}` : '';
      if (fitKey !== this.fitKey) {
        this.fitKey = fitKey;
        this.schedulePresentation();
      }
      this.lastTotal = show ? totalText : '';
      this.lastAddon = addonText;
      this.lastOptionsUnknown = options.unknown;
      this.purchaseTotal = { totalText, baseTotalText: this.money((plan ? plan.price : variant.price) * quantity + replace),
        optionsUnknown: options.unknown, note: note || '', available: this.isPurchasable(), callForPrice: Boolean(variant.callForPrice),
        buttonLabel: variant.available ? variant.addLabel : this.dataset.soldLabel, variantId: variant.id };
      this.dispatchEvent(new CustomEvent('cq:purchase-total', { bubbles: true, detail: this.purchaseTotal }));
      // What this total was worked out from (stale() compares it with the controls).
      this.renderedControls = this.controlsKey();
      this.queueStatus(`${variant.id}|${plan?.id || ''}|${totalText}|${options.unknown}`, baseline);
    }

    // r10 item 4: Shop Pay Installments under the price (snippets/cq-option-picker.liquid). Shown for a one-time
    // purchase of an option the widget marks eligible, hidden (display: none, nothing reserved) otherwise. It describes
    // the price above it, the option's one-time price. On the product page the widget sits in its own small product
    // form, whose variant id is set here and announced with a change event (the widget reads the id input of its form
    // on change); in the popup it sits inside this form and follows its id input by itself. A product with plans keeps
    // the line for the saving while subscribing, so only the widget toggles; otherwise the line goes with the widget.
    // INP: the installments line follows one frame after the tap. Shopify's payment terms element re-renders when its
    // hidden form changes, which used to run inside the tap (about 300 ms on a slow phone, most of the first tap). Only
    // the latest call runs; the line reads the same either way.
    scheduleTerms(variant, plan) {
      const token = (this.termsToken = (this.termsToken || 0) + 1);
      afterPaint().then(() => {
        if (token !== this.termsToken || !this.isConnected) return;
        this.syncTerms(variant, plan);
      });
    }

    syncTerms(variant, plan) {
      this.summaryAll('[data-cq-terms]').forEach((terms) => {
        const show = !plan && variant.available && !variant.callForPrice && this.termsEligible(terms, variant);
        const form = terms.querySelector('[data-cq-terms-form]');
        const input = form?.querySelector('input[name="id"]');
        if (input && input.value !== String(variant.id)) {
          input.value = variant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (terms.hidden === show) terms.hidden = !show;
        const line = terms.closest('[data-cq-subline]');
        if (line && !line.classList.contains('has-plans') && line.hidden === show) line.hidden = !show;
      });
    }

    // r8 item 27: the widget's own eligibility for this option (its shopify-meta lists every variant); without it,
    // Shopify's $50 minimum on the option's price.
    termsEligible(terms, variant) {
      if (this.termsMeta === undefined) {
        this.termsMeta = null;
        try {
          const meta = JSON.parse(terms.querySelector('shopify-payment-terms')?.getAttribute('shopify-meta') || 'null');
          if (Array.isArray(meta?.variants)) this.termsMeta = new Map(meta.variants.map((item) => [String(item.id), item.eligible !== false]));
        } catch (error) { /* no meta: the price rule below */ }
      }
      const eligible = this.termsMeta?.get(String(variant.id));
      return eligible === undefined ? variant.price >= 5000 : eligible;
    }

    // Too narrow for the label and the total: .is-tight hides the total visually (still in the button's name);
    // the label stays at the start edge. The two spans and the button are read in the shared measurement pass
    // (CQMeasure), with the guides and the SKU row, and the class is written after every read of that pass.
    fitTotal() {
      window.CQMeasure.queue(this.fitKeys.total, {
        read: () => {
          const button = this.fitTarget;
          const label = button?.querySelector('.cq-purchase-add-label');
          const total = button?.querySelector('[data-cq-add-total]');
          if (!this.isConnected || !label || !total || total.hidden) return null;
          const style = getComputedStyle(button);
          const content = button.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
          if (content <= 0) return null;
          return label.scrollWidth + (parseFloat(style.columnGap) || 12) + total.scrollWidth > content;
        },
        write: (tight) => {
          if (tight === null) return;
          this.querySelectorAll('.cq-purchase-add').forEach((element) => {
            if (element.classList.contains('is-tight') !== tight) element.classList.toggle('is-tight', tight);
          });
        },
      });
    }

    // One status line announces the latest variant, plan, quantity or app-option total after 300 ms of quiet.
    queueStatus(key, baseline) {
      if (baseline || !this.touched || this.statusKey === undefined) {
        if (baseline) clearTimeout(this.statusTimer);
        this.statusKey = key;
        return;
      }
      if (key === this.statusKey) return;
      this.statusKey = key;
      clearTimeout(this.statusTimer);
      this.statusTimer = setTimeout(() => {
        const status = this.querySelector('[data-cq-total-status]');
        if (!status || !this.lastTotal) return;
        const text = this.lastOptionsUnknown
          ? this.dataset.totalStatusOptions.replace('__TOTAL__', this.lastTotal)
          : this.lastAddon
          ? this.dataset.totalStatusAddons.replace('__TOTAL__', this.lastTotal).replace('__AMOUNT__', this.lastAddon)
          : this.dataset.totalStatus.replace('__TOTAL__', this.lastTotal);
        setText(status, text);
      }, 300);
    }

    // r8 item 30: this element plus the price block printed under the title ([data-cq-summary-for] names this
    // form; read as an attribute, since the form's id property is its name="id" input).
    summaryRoots() {
      const id = this.form.getAttribute('id') || '';
      return [this, ...document.querySelectorAll(`[data-cq-summary-for="${CSS.escape(id)}"]`)];
    }

    summaryAll(selector) {
      const found = new Set();
      this.summaryRoots().forEach((root) => root.querySelectorAll(selector).forEach((element) => found.add(element)));
      return [...found];
    }

    get purchaseInputs() {
      return {
        once: this.querySelector('.cq-purchase-types input[value="once"]'),
        subscribe: this.querySelector('.cq-purchase-types input[value="subscribe"]'),
      };
    }

    requiresPlan(variant = this.variant) {
      return Boolean(variant?.requiresPlan) || this.dataset.requiresPlan === 'true';
    }

    currentPlan() {
      const input = this.form.elements.selling_plan;
      if (!input || input.disabled || !input.value) return null;
      return this.variant.plans.find((plan) => String(plan.id) === input.value) || null;
    }

    // Release 1 lane C follow-up: the quantity and the replacement ticks a total was worked out from.
    controlsKey() {
      return `${this.form.elements.quantity?.value}|${this.replaceCards.map((entry) => `${entry.input.checked ? 1 : 0}:${entry.select?.value || ''}`).join('|')}`;
    }

    // Release 1 lane C follow-up: true when a control no longer shows what update() applied, which happens when the
    // browser restores form controls without a change event (Back without the back/forward cache, see connectedCallback).
    stale() {
      const variant = this.variant;
      if (this.form.elements.id.value !== String(variant.id)) return true;
      if (this.groups.some((group, index) => normalized(group.querySelector('input:checked')?.value) !== normalized(variant.options[index]))) return true;
      if (this.renderedControls !== this.controlsKey()) return true;
      const { once, subscribe } = this.purchaseInputs;
      if (!subscribe) return false;
      if ((!once?.checked && !subscribe.checked) || (once?.checked && once.disabled)) return true;
      const usePlan = variant.plans.length > 0 && (subscribe.checked || this.requiresPlan(variant));
      const planInput = this.form.elements.selling_plan;
      if (planInput.disabled === usePlan) return true;
      if (usePlan && planInput.value !== this.planChoice) return true;
      return [...this.querySelectorAll('[data-cq-plan-select]')].some((select) => select.value !== this.planChoice);
    }

    // Brings the selling plan input, the prices, the totals and the buttons back in line with what the controls show.
    resync() {
      if (!this.cqEvents || !this.variant || !this.stale()) return;
      // Browsers can restore a native delivery select without dispatching change. Treat that as the shopper's choice
      // before rebuilding the card and cart datasets; otherwise the visible "Order once" could submit a subscription.
      for (const entry of this.replaceCards) {
        if (entry.select && entry.renderedChoice !== undefined && entry.select.value !== entry.renderedChoice) {
          entry.choice = entry.select.value;
          entry.choiceTouched = true;
        }
      }
      this.update();
    }

    isPurchasable() {
      const variant = this.variant;
      if (!variant || !variant.available || variant.callForPrice) return false;
      const usingPlan = !this.form.elements.selling_plan.disabled;
      if ((usingPlan || this.requiresPlan()) && !this.currentPlan()) return false;
      return true;
    }

    // Release 1 lane Y (AJ 2026-10-11 05:48): the most popular setup in one tap. The button carries the option values
    // ([{i, v}], snippets/cq-option-picker.liquid); the variant with all of them that keeps the most of the shopper's
    // other choices (the pump) is chosen, else the first one in stock, and its values are checked as if picked.
    choosePopular(button) {
      let want;
      try { want = JSON.parse(button.dataset.cqPopular); } catch (error) { return; }
      if (!Array.isArray(want) || !want.length) return;
      const wanted = new Map(want.map((entry) => [Number(entry.i), normalized(entry.v)]));
      const current = this.variant?.options || [];
      const matches = this.variants.filter((v) => [...wanted].every(([i, value]) => normalized(v.options[i]) === value));
      const keeps = (v) => v.options.every((value, i) => wanted.has(i) || normalized(value) === normalized(current[i]));
      const variant = matches.find((v) => keeps(v) && v.available) || matches.find(keeps) || matches.find((v) => v.available) || matches[0];
      if (!variant) return;
      let last = null;
      this.groups.forEach((group) => {
        const i = Number(group.dataset.cqOption);
        const input = [...group.querySelectorAll('input')].find((item) => normalized(item.value) === normalized(variant.options[i]));
        if (input && !input.checked) { input.checked = true; last = input; }
      });
      if (last) this.selectVariant(last);
    }

    // The variants holding this normalized value for this option, in catalogue order (connectedCallback).
    withValue(index, value) {
      return this.byValue.get(`${index}|${value}`) || [];
    }

    // The variant with exactly these normalized values, if any (selected: one value per group).
    exactVariant(selected) {
      return this.variants.find((v) => { const norm = norms(v); return selected.every((value, i) => norm[i] === value); });
    }

    choiceVariant(selected, index) {
      return this.exactVariant(selected) || this.nearestVariant(selected, index);
    }

    // r10 item 6: the nearest compatible variant. It keeps the value just chosen, then as many of the other
    // choices as possible, then one in stock, then the closest values in each switched option's own order (Brass
    // Rotary at 4000 GPD goes to 2500 GPD, the largest flow rate that pump comes with, not to 300 GPD).
    nearestVariant(selected, index) {
      const orders = this.orders;
      const rank = (v) => {
        const norm = norms(v);
        let kept = 0;
        let distance = 0;
        selected.forEach((value, i) => {
          if (i === index) return;
          const own = norm[i];
          if (own === value) kept += 1;
          else distance += Math.abs(orders[i].indexOf(own) - orders[i].indexOf(value));
        });
        return [kept, v.available ? 1 : 0, -distance];
      };
      const better = (a, b) => {
        for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return a[i] > b[i];
        return false;
      };
      let best = null;
      this.withValue(index, selected[index]).forEach((v) => {
        const score = rank(v);
        if (!best || better(score, best.score)) best = { v, score };
      });
      return best?.v;
    }

    selectVariant(input) {
      const selected = this.groups.map((group) => { const checked = group.querySelector('input:checked'); return checked ? normInput(checked) : ''; });
      const index = Number(input.closest('[data-cq-option]').dataset.cqOption);
      const exact = this.exactVariant(selected);
      const previous = this.variant;
      const variant = exact || this.nearestVariant(selected, index);
      if (!variant) return;
      this.variant = variant;
      this.writeCallouts(exact ? null : index, variant, previous);
      this.update();
      const gallery = this.closest('.cq-product-page')?.querySelector('media-gallery');
      // INP: the tap paints first. The choice, price, button and callouts above are drawn now. Everything else follows
      // in short tasks after the paint, one task each, so no single follow-up holds the main thread and a second tap can
      // land between them: first the photo (the gallery swap and its carousel re-read), then the address (the Meta
      // pixel's replaceState hook, about 285 ms on a slow phone) and the popup links, then the listeners (SC Product
      // Options, related parts, the sticky bars). Only the latest tap runs them, so quick taps never announce an older
      // option.
      const token = (this.followToken = (this.followToken || 0) + 1);
      const current = () => token === this.followToken && this.variant === variant && this.isConnected;
      (async () => {
        await afterPaint();
        if (!current()) return;
        if (gallery && !gallery.showVariantPhotos?.(variant) && variant.mediaId) gallery.setActiveMedia?.(variant.mediaId, false, true);
        await afterPaint();
        if (!current()) return;
        if (this.dataset.updateUrl === 'true') {
          const url = new URL(location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState(history.state, '', url);
        }
        // The popup's "View full details" and (r10 item 7) "More add-ons on the full page" open the chosen option.
        this.querySelectorAll('[data-cq-details]').forEach((details) => {
          const url = new URL(details.href, location.origin);
          url.searchParams.set('variant', variant.id);
          details.href = url.pathname + url.search;
        });
        await afterPaint();
        if (!current()) return;
        this.form.elements.id.dispatchEvent(new Event('change', { bubbles: true }));
        this.dispatchEvent(new CustomEvent('cq:variant-change', { bubbles: true, detail: { variant } }));
        document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: { ...variant, featured_media: { id: variant.mediaId } } } }));
      })();
    }

    // r10 item 6: one callout under each group the last pick switched, built from the real option names and values:
    // "We switched Pump to 1.5 HP Horizontal Pump, because 4000 GPD comes only with it." when the picked value comes
    // with one value of that option alone (given the other choices), else "We switched Flow Rate to 2500 GPD, because
    // Brass Rotary is not offered with 4000 GPD." Every callout is cleared by the next option change (picked: null).
    // AJ 2026-10-18 15:06 ("we dont need this We switched Fitting to JCO, because Inline/Sealed is not offered with
    // Standard ... just do it"): the switch still happens, silently. Any old callout is cleared and no new one is written.
    writeCallouts(picked, variant, previous) {
      if (!SWITCH_CALLOUTS) {
        this.groups.forEach((group) => setText(group.querySelector('[data-cq-callout]'), ''));
        return;
      }
      this.groups.forEach((group, i) => {
        const callout = group.querySelector('[data-cq-callout]');
        if (!callout) return;
        let text = '';
        if (picked !== null && i !== picked && previous && normalized(variant.options[i]) !== normalized(previous.options[i])) {
          const name = (group.querySelector('.cq-purchase-legend-name')?.firstChild?.textContent || '').trim();
          const values = new Set(this.variants
            .filter((v) => v.options.every((value, k) => k === i || normalized(value) === normalized(variant.options[k])))
            .map((v) => normalized(v.options[i])));
          const template = values.size === 1 ? this.dataset.switchedOnly : this.dataset.switched;
          text = (template || '').replace('__OPTION__', name).replace('__VALUE__', variant.options[i])
            .replace('__CHOSEN__', variant.options[picked]).replace('__PREVIOUS__', previous.options[i]);
        }
        setText(callout, text);
      });
    }

    // r12d: Buy now stays in place for one-time, subscription and replacement purchases; only availability changes.
    syncCheckout() {
      const checkout = this.querySelector('[data-cq-checkout]');
      if (!checkout) return;
      const purchasable = this.isPurchasable();
      checkout.inert = !purchasable;
      checkout.classList.toggle('is-unavailable', !purchasable);
      const buyNow = checkout.querySelector('[data-cq-buy-now]');
      if (buyNow) buyNow.disabled = !purchasable;
    }

    // The checkbox opts into an add-on; its independent selector only changes how that add-on would be purchased.
    chooseReplacePlan(select) {
      const entry = this.replaceCards.find((one) => one.select === select);
      if (!entry || !this.variant) return;
      const key = entry.data.map?.[String(this.variant.id)];
      const item = key ? entry.data.items?.[String(key).split('@')[0]] : null;
      if (!item || !(select.value === 'once' ? item.once && !item.requiresPlan : item.plans?.some((plan) => String(plan.plan) === select.value))) return;
      entry.choice = select.value;
      entry.choiceTouched = true;
      this.syncReplace(this.variant);
      this.syncCheckout();
      this.renderTotal();
    }

    replaceChoice(entry, item) {
      const plans = item.plans || [];
      const once = !item.requiresPlan ? item.once : null;
      // Remember a manual choice across compatible parent options. An unavailable plan uses this cartridge's valid
      // default; the old id is never copied into its cart payload. Returning to a compatible option restores the choice.
      return (entry.choice === 'once' ? once : plans.find((plan) => String(plan.plan) === entry.choice))
        || plans.find((plan) => String(plan.plan) === String(item.plan)) || plans[0] || once || null;
    }

    // r10 item 13: the cartridge option that goes with this option of the parent (data-cq-replace-data, from the
    // snippet's rules), or no card when it is unclear. Only what differs is written.
    // r11 item 3: per card; the block hides only when no card is left for this option.
    // Round 4 fix pass item 10: the cards follow the option's own parts list, and a card can carry a count ("Qty 3",
    // item.qty, sent as data-quantity by product-form.js). The "What's included?" list follows the option too.
    syncReplace(variant) {
      this.syncIncluded(variant);
      if (!this.replace) return;
      for (const entry of this.replaceCards) {
        const { card, input, select, data } = entry;
        const id = data.map?.[String(variant.id)];
        const item = id ? data.items?.[String(id).split('@')[0]] : null;
        const chosen = item ? this.replaceChoice(entry, item) : null;
        if (card.hidden === Boolean(chosen)) card.hidden = !chosen;
        const qty = String(item?.qty || 1);
        if (!item || !chosen) continue;
        Object.assign(input.dataset, { variantId: item.id, quantity: qty, sellingPlan: chosen.plan ? String(chosen.plan) : '', price: chosen.price, image: item.image || '' });
        if (select) {
          if (entry.renderedItem !== item) {
            const choices = [...(item.plans || []), ...(!item.requiresPlan && item.once ? [item.once] : [])];
            select.replaceChildren(...choices.map((choice) => Object.assign(document.createElement('option'), {
              value: choice.plan ? String(choice.plan) : 'once', textContent: `${choice.note} · ${choice.priceText}`
            })));
            select.hidden = choices.length < 2;
            entry.renderedItem = item;
          }
          select.value = chosen.plan ? String(chosen.plan) : 'once';
          entry.renderedChoice = select.value;
        }
        const count = card.querySelector('[data-cq-replace-qty]');
        if (count) {
          if (count.hidden !== (Number(qty) < 2)) count.hidden = Number(qty) < 2;
          setText(count.querySelector('[data-cq-replace-qty-n]'), qty);
        }
        setText(card.querySelector('[data-cq-replace-price]'), chosen.priceText);
        setText(card.querySelector('[data-cq-replace-note]'), chosen.note);
        const priceUnit = card.querySelector('[data-cq-replace-price-unit]');
        if (priceUnit) priceUnit.hidden = !chosen.perDelivery;
        const save = card.querySelector('[data-cq-replace-save]');
        if (save) {
          setText(save, chosen.save || '');
          save.hidden = !chosen.save;
        }
        setText(card.querySelector('[data-cq-replace-option]'), item.option || '');
        const dot = card.querySelector('.cq-replace-dot');
        if (dot) dot.hidden = !item.option;
        const image = card.querySelector('[data-cq-replace-image]');
        if (image && item.image && image.getAttribute('src') !== item.image) image.src = item.image;
        if (image) image.hidden = !item.image;
      }
      // The shown cards keep the order of the option's own list ('@n' in the map); only a real change moves them.
      const byCard = new Map(this.replaceCards.map((entry) => [entry.card, entry]));
      const shown = [...this.replace.querySelectorAll('[data-cq-replace-card]:not([hidden])')].map((card) => byCard.get(card)).filter(Boolean)
        .map((entry) => ({ entry, pos: Number(String(entry.data.map?.[String(variant.id)] ?? '').split('@')[1]) }));
      if (shown.length > 1 && shown.every((one) => Number.isFinite(one.pos))) {
        const sorted = [...shown].sort((a, b) => a.pos - b.pos);
        if (sorted.some((one, index) => one !== shown[index])) sorted.forEach((one) => one.entry.card.parentElement?.append(one.entry.card));
      }
      const empty = this.replaceCards.every((entry) => entry.card.hidden);
      if (this.replace.hidden !== empty) this.replace.hidden = empty;
      this.syncReplaceAll();
    }

    // Round 4 fix pass item 10: "What's included?" (snippets/cq-pdp-buyer-facts.liquid) prints one parts list per set of
    // options that share it; data-cq-included-for names those options, and only the chosen option's list shows.
    syncIncluded(variant) {
      const key = `,${variant.id},`;
      this.querySelectorAll('[data-cq-included-for]').forEach((list) => {
        const hidden = !list.dataset.cqIncludedFor.includes(key);
        if (list.hidden !== hidden) list.hidden = hidden;
      });
    }

    // AJ 2026-10-11 (option A): Loop's live defaults (ids, from assets/cq-loop-default.js) against what the page started
    // on. When Loop marks a different plan of the same group as its default (the theme's copy of Loop's defaults is out
    // of date), the frequency follows Loop through update(), so the prices, the labels, the selling_plan input and the
    // cart line stay in step, and so do the replacement cards that are not ticked (through syncReplace). Nothing moves
    // once the shopper has touched the picker, for a plan named by the link, or when Loop marks no plan of that group.
    // An eligible category default is deliberate, so Loop's group-wide default must not replace it.
    followLoop(ids) {
      const loop = window.CQLoopDefaults;
      if (!loop || this.touched || this.planTouched) return;
      const variant = this.variant;
      if (variant && !this.planFromLink && !variant.plans.some((plan) => String(plan.id) === String(variant.categoryDefaultPlan))) {
        const current = variant.plans.find((plan) => String(plan.id) === this.planChoice);
        const target = current ? loop.pick(variant.plans, current.group, ids) : null;
        if (target && String(target.id) !== this.planChoice) {
          this.planChoice = String(target.id);
          this.update();
        }
      }
      let moved = false;
      for (const { input, data, choiceTouched } of this.replaceCards) {
        if (input.checked || choiceTouched) continue;
        for (const item of Object.values(data.items || {})) {
          if (!Array.isArray(item.plans)) continue;
          if (item.plans.some((plan) => String(plan.plan) === String(item.categoryDefaultPlan))) continue;
          const current = item.plans.find((plan) => String(plan.plan) === String(item.plan));
          const target = current ? item.plans.find((plan) => String(plan.group) === String(current.group) && ids.has(String(plan.plan))) : null;
          if (!target || target === current) continue;
          Object.assign(item, { plan: target.plan, price: target.price, priceText: target.priceText, note: target.note, save: target.save, perDelivery: target.perDelivery });
          moved = true;
        }
      }
      if (moved && variant) this.syncReplace(variant);
    }

    // r11 item 3: "Add all" shows with two or more cards; it reads "Remove all" while every card that shows is ticked.
    syncReplaceAll(announce = this.touched) {
      this.syncReplaceCarousel(announce);
      const button = this.replace?.querySelector('[data-cq-replace-all]');
      if (!button) return;
      const shown = this.replaceShown();
      const many = shown.length > 1;
      if (button.hidden === many) button.hidden = !many;
      const remove = many && shown.every((entry) => entry.input.checked);
      const carousel = this.replace.hasAttribute('data-cq-replace-carousel');
      const carouselText = remove ? button.dataset.carouselRemoveText : button.dataset.carouselAddText;
      const text = carousel && carouselText ? carouselText.replace('__COUNT__', String(shown.length)) : remove ? button.dataset.removeText : button.dataset.addText;
      const label = carousel ? text : remove ? button.dataset.removeLabel : button.dataset.addLabel;
      if (text && button.textContent !== text) button.textContent = text;
      if (label && button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
    }

    // Eligibility stays on card.hidden: totals and product-form must still include selected offscreen cards.
    // Query DOM order because a parent option can reorder the same cartridge slots in syncReplace().
    replaceOrdered() {
      const eligible = new Map(this.replaceShown().map((entry) => [entry.card, entry]));
      return [...(this.replace?.querySelectorAll('[data-cq-replace-card]') || [])].map((card) => eligible.get(card)).filter(Boolean);
    }

    syncReplaceCarousel(announce = this.touched) {
      if (!this.replace) return;
      const shown = this.replaceOrdered();
      const previous = this.replace.querySelector('[data-cq-replace-prev]');
      const next = this.replace.querySelector('[data-cq-replace-next]');
      const status = this.replace.querySelector('[data-cq-replace-carousel-status]');
      const footer = this.replace.querySelector('[data-cq-replace-carousel-footer]');
      // Incomplete/older markup remains the usable list, even with this script already loaded.
      const active = Boolean(shown.length > 3 && previous && next && status && footer);
      const current = shown.find((entry) => entry.card === this.replaceCarouselCard) || shown[0];
      this.replaceCarouselCard = current?.card || null;
      const focus = document.activeElement;
      const cardLosingFocus = this.replaceCards.some(({ card }) => card.contains(focus) && (card.hidden || (active && card !== current?.card)));
      const controlsLosingFocus = !active && [previous, next, footer].some((node) => node?.contains(focus));
      this.replace.toggleAttribute('data-cq-replace-carousel', active);
      for (const { card } of this.replaceCards) {
        const offscreen = active && !card.hidden && card !== current?.card;
        card.toggleAttribute('data-cq-replace-offscreen', offscreen);
        card.toggleAttribute('inert', offscreen);
      }
      for (const node of [previous, next, footer]) if (node) node.hidden = !active;
      if (status) {
        // A populated status is useful visually at load without announcing an unsolicited carousel update.
        status.setAttribute('aria-live', announce ? 'polite' : 'off');
        const count = shown.filter((entry) => entry.input.checked).length;
        const position = (status.dataset.positionText || '').replace('__CURRENT__', String(shown.indexOf(current) + 1)).replace('__COUNT__', String(shown.length));
        const selection = count ? (status.dataset.selectedText || '').replace('__COUNT__', String(count)) : status.dataset.noneText || '';
        setText(status, active ? [position, selection].filter(Boolean).join(' · ') : '');
      }
      if (cardLosingFocus || controlsLosingFocus) {
        const target = current?.input || this.querySelector('[data-cq-option] input:not(:disabled), [data-cq-option] button:not(:disabled)') || this.form?.querySelector('[type="submit"]');
        target?.focus({ preventScroll: true });
      }
    }

    moveReplace(direction) {
      if (!this.replace?.hasAttribute('data-cq-replace-carousel')) return;
      const shown = this.replaceOrdered();
      if (!shown.length) return;
      const index = shown.findIndex((entry) => entry.card === this.replaceCarouselCard);
      this.replaceCarouselCard = shown[(index + direction + shown.length) % shown.length].card;
      this.syncReplaceAll(true);
    }

    replaceShown() {
      return this.replace && !this.replace.hidden ? this.replaceCards.filter((entry) => !entry.card.hidden) : [];
    }

    replaceTicked() {
      return this.replaceShown().filter((entry) => entry.input.checked && entry.input.dataset.variantId);
    }

    // Every ticked card, one cartridge each on its plan price, whatever the quantity.
    replaceCents() {
      return this.replaceTicked().reduce((sum, entry) => sum + (Number(entry.input.dataset.price) || 0), 0);
    }

    // v5.5: both SKU locations and fit reads follow the price/photo paint. Coalesce rapid changes and read
    // the current variant when the task runs, so an older selection can never restore an older SKU.
    schedulePresentation() {
      if (this.presentationPending) return;
      this.presentationPending = true;
      const token = this.presentationToken = (this.presentationToken || 0) + 1;
      afterPaint().then(() => {
        if (token !== this.presentationToken || !this.isConnected) return;
        this.presentationPending = false;
        const sku = this.variant?.sku ? `${this.dataset.skuLabel} ${this.variant.sku}` : '';
        document.querySelectorAll(`[data-cq-sku-for="${CSS.escape(this.form.getAttribute('id') || '')}"]`).forEach((element) => setText(element, sku));
        this.fitTotal();
        this.fitSku();
      });
    }

    // On a phone the price row drops its SKU (data-cq-sku-cramped, cq-product-page.css) when the row's parts do not fit
    // on one line beside a struck price with the installments line under it. The cramped state hides the SKU, so it
    // comes off in the shared pass's clear step before the row is measured; the rows that still need it get it back in
    // the write step of the same task, so nothing paints in between.
    fitSku() {
      let rows = [];
      window.CQMeasure.queue(this.fitKeys.sku, {
        clear: () => {
          rows = this.isConnected ? this.summaryAll('.cq-purchase-price').filter((row) => row.querySelector('.cq-pdp-mobile-sku')) : [];
          rows.forEach((row) => row.removeAttribute('data-cq-sku-cramped'));
        },
        read: () => rows.filter((row) => {
          const sku = row.querySelector('.cq-pdp-mobile-sku');
          if (!window.matchMedia('(max-width: 749px)').matches || !sku.textContent.trim() ||
              !row.querySelector('[data-cq-compare]:not([hidden])') ||
              !row.closest('.cq-purchase-summary')?.querySelector('[data-cq-terms]:not([hidden])')) return false;
          const items = [...row.children].filter((node) => !node.hidden && getComputedStyle(node).display !== 'none');
          const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
          const needed = items.reduce((width, node) => width + node.getBoundingClientRect().width, 0) + gap * Math.max(0, items.length - 1);
          return needed > row.clientWidth + 1;
        }),
        write: (cramped) => cramped.forEach((row) => row.setAttribute('data-cq-sku-cramped', '')),
      });
    }

    // The product page row names the cadence only ("Every 12 months"); Configure adds each allocation's price.
    renderPlans(select, variant) {
      select.replaceChildren();
      const single = this.planGroups.length === 1;
      for (const group of this.planGroups) {
        const allocations = variant.plans.filter((plan) => String(plan.group) === String(group.id));
        if (!allocations.length) continue;
        const parent = single ? select : document.createElement('optgroup');
        if (!single) { parent.label = group.name; select.append(parent); }
        for (const allocation of allocations) {
          const text = this.planNote(allocation);
          parent.append(new Option(select.closest('.cq-purchase-subrow--polish') ? text.charAt(0).toUpperCase() + text.slice(1) : `${text} · ${allocation.priceText}`, allocation.id));
        }
      }
    }

    // Release 1 lane U: the frequency picked is the plan; picked while the Subscribe & save switch is off, it also
    // switches the subscription on.
    choosePlan(select) {
      this.planChoice = select.value;
      this.planTouched = true;
      const { subscribe } = this.purchaseInputs;
      if (subscribe && !subscribe.disabled) subscribe.checked = true;
      this.update();
    }

    planNote(plan) {
      if (!plan) return '';
      const name = plan.name.trim();
      const lower = name.toLowerCase().replace(/^.*\bdelivered\s+/, '').trim();
      // AJ 2026-10-12: words around the frequency ("Fresh membranes every 24 months") still read "Delivered every 24 months".
      const every = lower.match(/every\s+(.+)$/);
      if (every && every[1].trim()) return this.dataset.noteEvery.replace('__PLAN__', every[1].trim());
      if (/^\d/.test(lower)) return this.dataset.noteEvery.replace('__PLAN__', lower);
      if (/^every\s/.test(lower) || /^(daily|weekly|biweekly|fortnightly|monthly|bimonthly|quarterly|yearly|annually)$/.test(lower)) {
        return this.dataset.notePlain.replace('__PLAN__', lower);
      }
      return this.dataset.noteNamed.replace('__PLAN__', name);
    }

    update() {
      const variant = this.variant;
      if (!variant) return;
      this.form.elements.id.value = variant.id;

      const { once, subscribe } = this.purchaseInputs;
      const requiresPlan = this.requiresPlan(variant);
      if (once) once.disabled = requiresPlan;
      if (subscribe) {
        // v5: the Subscribe & save switch (no "once" input any more); a required plan keeps it on and locked.
        subscribe.disabled = !variant.plans.length || requiresPlan;
        if (requiresPlan && variant.plans.length) subscribe.checked = true;
        else if (!variant.plans.length && once) once.checked = true;
        // Release 1 lane C follow-up: never neither (a restored group can come back with no radio checked).
        else if (once && !once.checked && !subscribe.checked) once.checked = true;
      }
      const usePlan = Boolean(subscribe?.checked && variant.plans.length) || (requiresPlan && variant.plans.length > 0);

      // Keep the selected allocation across variants; the frequency select hides when there is no choice.
      const planInput = this.form.elements.selling_plan;
      const shownPlan = variant.plans.find((p) => String(p.id) === this.planChoice) || preferredPlan(variant.plans, this.planGroups, variant.categoryDefaultPlan);
      this.planChoice = shownPlan ? String(shownPlan.id) : '';
      const choices = variant.plans.length > 1;
      this.querySelectorAll('[data-cq-plan-select]').forEach((select) => {
        if (this.renderedVariant !== variant.id) this.renderPlans(select, variant);
        if (select.value !== this.planChoice) select.value = this.planChoice;
        if (select.hidden === choices) select.hidden = !choices;
        const face = select.previousElementSibling;
        if (choices) { if (face && face.getAttribute('aria-hidden') !== 'true') face.setAttribute('aria-hidden', 'true'); } else face?.removeAttribute('aria-hidden');
      });
      const planWrap = this.querySelector('[data-cq-plan-wrap]');
      if (planWrap && planWrap.hidden === usePlan) planWrap.hidden = !usePlan;
      const onceNote = this.querySelector('[data-cq-once-note]');
      if (onceNote && onceNote.hidden !== usePlan) onceNote.hidden = usePlan;
      const plan = usePlan ? shownPlan : null;
      planInput.disabled = !plan;
      planInput.value = plan ? plan.id : '';
      // The Subscribe & save row shows the plan it would use; v5: its label carries the plan's whole-number saving
      // ("Subscribe & save 5%", savePercent from the plans JSON), else the plain labels.
      const planTitle = this.querySelector('[data-cq-plan-title]');
      if (planTitle) {
        const percent = Number(shownPlan?.savePercent) || 0;
        const label = percent && planTitle.dataset.labelPercent ? planTitle.dataset.labelPercent.replace('__PERCENT__', String(percent))
          : shownPlan && shownPlan.price < variant.price ? planTitle.dataset.labelSaving : planTitle.dataset.labelSubscribe;
        setText(planTitle, label);
        if (subscribe && subscribe.getAttribute('aria-label') !== label) subscribe.setAttribute('aria-label', label);
      }
      setText(this.querySelector('[data-cq-plan-price]'), shownPlan ? shownPlan.priceText : this.dataset.subscribeUnavailable);
      const planPriceUnit = this.querySelector('[data-cq-plan-price-unit]');
      const planUnitHidden = !shownPlan?.perDelivery;
      if (planPriceUnit && planPriceUnit.hidden !== planUnitHidden) planPriceUnit.hidden = planUnitHidden;
      // Release 1 lane C: the Subscribe card also names that plan's real saving ("Save 5%", from the plans JSON) and its
      // period, in both purchase types. Lane C3 (Jolie): the one-time price, struck through, sits beside the plan price
      // whenever the plan costs less.
      const planCompare = this.querySelector('[data-cq-plan-compare]');
      if (planCompare) {
        const struck = Boolean(shownPlan && shownPlan.price < variant.price);
        setText(planCompare.querySelector('[data-cq-plan-compare-text]'), struck ? variant.priceText : '');
        if (planCompare.hidden !== !struck) planCompare.hidden = !struck;
      }
      const planSave = this.querySelector('[data-cq-plan-save]');
      if (planSave) {
        const saveText = shownPlan?.saveBadge || '';
        setText(planSave.querySelector('[data-cq-plan-save-text]'), saveText);
        if (planSave.hidden !== !saveText) planSave.hidden = !saveText;
      }
      const planPeriod = this.querySelector('[data-cq-plan-period]');
      if (planPeriod) {
        const periodText = this.planNote(shownPlan);
        setText(planPeriod.querySelector('[data-cq-plan-period-text]'), periodText);
        if (planPeriod.hidden !== !periodText) planPeriod.hidden = !periodText;
      }

      const current = norms(variant);
      const optionNames = this.groups.map((group) => (group.querySelector('.cq-purchase-legend-name')?.firstChild?.textContent || '').trim());
      const hideSingleOptions = this.hasAttribute('data-hide-single-options');
      this.groups.forEach((group, index) => {
        // r8 item 24: the legend's "Name  Value" follows the selected option (same frame as the change).
        setText(group.querySelector('[data-cq-legend-value]'), String(variant.options[index] ?? ''));
        if (hideSingleOptions) {
          const validValues = new Set(this.variants
            .filter((candidate) => norms(candidate).every((value, candidateIndex) => candidateIndex === index || value === current[candidateIndex]))
            .map((candidate) => norms(candidate)[index]));
          const hide = index > 0 && validValues.size <= 1;
          if (group.hidden !== hide) group.hidden = hide;
        }
        const priced = group.hasAttribute('data-cq-prices');
        // The group's inputs with their normalized values and the variants holding each value come precomputed from
        // connectedCallback. Only what differs is written, so observers (the session recorder among them) see real changes.
        this.groupInputs[index].forEach((input) => {
          const value = input.cqNorm;
          const checked = value === current[index];
          if (input.checked !== checked) input.checked = checked;
          // Same other choices, this value: does it exist, and is it in stock?
          const matches = this.withValue(index, value).filter((v) => norms(v).every((other, i) => i === index || other === current[i]));
          // r10 item 6: a value that does not exist with the other choices is not greyed (picking it switches the
          // other option, see selectVariant); only a value whose combinations are all sold out looks sold out.
          const missing = matches.length === 0;
          const sold = !missing && !matches.some((v) => v.available);
          const label = input.closest('label');
          const hideLabel = hideSingleOptions && index > 0 && missing;
          if (label.hidden !== hideLabel) label.hidden = hideLabel;
          if (label.classList.contains('is-missing') !== missing) label.classList.toggle('is-missing', missing);
          if (label.classList.contains('is-soldout') !== sold) label.classList.toggle('is-soldout', sold);
          if (sold) { if (input.getAttribute('aria-description') !== this.dataset.soldLabel) input.setAttribute('aria-description', this.dataset.soldLabel); }
          else if (input.hasAttribute('aria-description')) input.removeAttribute('aria-description');
          // Reuse the click resolver so the note describes the actual configuration this choice selects.
          if (priced) {
            const slot = label.querySelector('[data-cq-value-price]');
            const wanted = current.slice();
            wanted[index] = value;
            const match = this.choiceVariant(wanted, index);
            const presentation = optionChoiceNote(variant, match, index, plan, Boolean(match && this.requiresPlan(match)),
              optionNames, this.dataset);
            if (slot) {
              setText(slot.querySelector('[data-cq-value-price-text]'), presentation.text);
              setText(label.querySelector('[data-cq-value-price-spoken]'), presentation.spoken);
              const hideSlot = !presentation.text;
              if (slot.hidden !== hideSlot) slot.hidden = hideSlot;
            }
          }
        });
      });

      const purchasable = this.isPurchasable();
      const callForPrice = Boolean(variant.callForPrice);
      // Bold Product Options clones the add button once; keep every copy in step.
      this.querySelectorAll('.cq-purchase-add').forEach((button) => {
        if (button.disabled !== !purchasable) button.disabled = !purchasable;
        if (button.hidden !== callForPrice) button.hidden = callForPrice;
        setText(button.querySelector('[data-cq-submit-label]') || button.querySelector('span'), variant.available ? variant.addLabel : this.dataset.soldLabel);
      });
      const callPrice = this.querySelector('[data-cq-call-price]');
      if (callPrice.hidden !== !callForPrice) callPrice.hidden = !callForPrice;
      const quantityWrap = this.querySelector('[data-cq-quantity-wrap]');
      // 2026-10-13: a system's stepper in the Configure popup stays hidden (data-cq-quantity-off; cq-option-picker.liquid).
      if (quantityWrap) {
        const quantityHidden = callForPrice || quantityWrap.hasAttribute('data-cq-quantity-off');
        if (quantityWrap.hidden !== quantityHidden) quantityWrap.hidden = quantityHidden;
      }
      const headUnit = this.syncPacks(variant, plan);
      this.syncShipNotes(variant);
      this.syncRoTankGuide(variant);
      this.syncReplace(variant);
      this.syncCheckout();

      // r8 item 30: the price block sits under the title, outside this element on the product page
      // (summaryRoots). Text is written only when it changes; the total status announces after a short debounce.
      // Release 1 lane C3 (Jolie): while the plan price is the headline, the struck price is the one-time price;
      // otherwise the compare-at price, when there is one (the same rule as head_compare in cq-option-picker.liquid).
      const priceText = plan ? plan.priceText : variant.priceText;
      const onceStruck = Boolean(plan && plan.price < variant.price);
      const compareHidden = !onceStruck && !(variant.compare > (plan ? plan.price : variant.price));
      const compareText = compareHidden ? '' : (onceStruck ? variant.priceText : variant.compareText);
      // v5: with one-time purchase chosen, the grey line names what the Subscribe & save row would save.
      const offerPlan = !plan && shownPlan && shownPlan.price < variant.price && shownPlan.savingMoney ? shownPlan : null;
      const savingText = plan && plan.price < variant.price ? plan.savingText
        : offerPlan && this.dataset.savingSubscribe ? this.dataset.savingSubscribe.replace('__AMOUNT__', offerPlan.savingMoney) : '';
      this.summaryAll('[data-cq-price]').forEach((element) => setText(element, priceText));
      this.summaryAll('[data-cq-price-unit]').forEach((element) => {
        const hidden = !headUnit;
        if (element.hidden !== hidden) element.hidden = hidden;
        setText(element.querySelector('[data-cq-price-unit-text]'), headUnit ? this.dataset.priceUnit.replace('__UNIT__', headUnit.text) : '');
        setText(element.querySelector('[data-cq-price-unit-label]'), headUnit ? headUnit.label : '');
      });
      this.summaryAll('[data-cq-compare]').forEach((element) => {
        if (element.hidden !== compareHidden) element.hidden = compareHidden;
        setText(element.querySelector('[data-cq-compare-text]'), compareText);
      });
      this.summaryAll('[data-cq-saving]').forEach((element) => setText(element, savingText));
      this.scheduleTerms(variant, plan);
      setText(this.querySelector('[data-cq-once-price]'), variant.priceText);
      this.schedulePresentation();
      this.querySelectorAll('[data-cq-sizing-option]').forEach((element) => {
        const matchesSize = String(variant.options[Number(element.dataset.cqSizingOption)] ?? '') === element.dataset.cqSizingValue;
        const matchesSoftener = !element.hasAttribute('data-cq-softener-option') ||
          String(variant.options[Number(element.dataset.cqSoftenerOption)] ?? '') === element.dataset.cqSoftenerValue;
        const hide = !matchesSize || !matchesSoftener;
        if (element.hidden !== hide) element.hidden = hide;
      });
      // r8 item 20: only exceptions (sold out, pre-order, backorder, low stock) are printed; there is no "In stock".
      const soldOut = !variant.available;
      this.summaryAll('[data-cq-availability]').forEach((availability) => {
        setText(availability, variant.stock || '');
        if (availability.classList.contains('is-sold') !== soldOut) availability.classList.toggle('is-sold', soldOut);
      });

      const quantity = this.form.elements.quantity;
      const min = String(variant.quantity?.min || 1);
      const step = String(variant.quantity?.increment || 1);
      if (quantity.min !== min) quantity.min = min;
      if (quantity.step !== step) quantity.step = step;
      if (variant.quantity?.max) { const max = String(variant.quantity.max); if (quantity.max !== max) quantity.max = max; } else quantity.removeAttribute('max');
      this.syncQuantity(false);
      this.renderedVariant = variant.id;
      this.renderTotal({ baseline: !this.touched });
    }

    syncRoTankGuide(variant) {
      const guide = this.querySelector('cq-option-guide[data-cq-guide="tank"]');
      if (!guide) return;
      const flowLabel = String(variant.options?.[0] || '').trim();
      const match = flowLabel.match(/^([\d,]+(?:\.\d+)?)\s+GPD$/i);
      const gpd = match ? Number(match[1].replace(/,/g, '')) : 0;
      if (!Number.isFinite(gpd) || gpd <= 0) return;
      setText(guide.querySelector('[data-cq-ro-guide-gpd]'), flowLabel);
      guide.querySelectorAll('[data-cq-ro-tank-refill]').forEach((element) => {
        const gallons = Number(element.dataset.cqRoTankRefill);
        if (!Number.isFinite(gallons) || gallons <= 0) return;
        const hours = gallons * 24 / gpd;
        const amount = hours >= 1 ? Math.round(hours * 10) / 10 : Math.round(hours * 60);
        const unit = hours >= 1 ? (amount === 1 ? 'hour' : 'hours') : (amount === 1 ? 'minute' : 'minutes');
        setText(element, `${amount} ${unit}`);
      });
    }

    // Release 1 lane Z1 (AJ 2026-10-11 06:02): the shipping note under the group that holds the 500 gallon tank kit
    // (snippets/cq-option-picker.liquid, ship_json) shows only while one of its values is chosen, whichever way it was
    // picked (a chip, the one-tap popular setup, a switched option). The text is written when it opens, so the polite
    // status announces it once; it opens and closes by height (cq-option-picker.css), and the text is cleared after
    // it has closed, so a closed note holds nothing for a screen reader and takes no space.
    syncShipNotes(variant) {
      this.querySelectorAll('[data-cq-ship-note]').forEach((note) => {
        let values = [];
        try { values = JSON.parse(note.dataset.cqShipNote).map(normalized); } catch (error) { return; }
        const index = Number(note.closest('[data-cq-option]')?.dataset.cqOption);
        const show = values.includes(normalized(variant.options[index]));
        const text = note.querySelector('[data-cq-ship-note-text]');
        clearTimeout(note.cqShipTimer);
        if (show) {
          setText(text, note.dataset.cqShipText || '');
          if (!note.classList.contains('is-shown')) note.classList.add('is-shown');
        } else if (note.classList.contains('is-shown')) {
          note.classList.remove('is-shown');
          note.cqShipTimer = setTimeout(() => { if (!note.classList.contains('is-shown')) setText(text, ''); }, 280);
        } else {
          setText(text, '');
        }
      });
    }

    // Release 1 lane U: a pack size group's unit prices, by the rule in the snippet (pack_group): each pack's variant
    // with the other options as chosen, using the plan that selecting that pack will choose, rounded up to the cent.
    // A pack with no plans uses its one-time price. Shown Amazon style as "$24.75/unit" and read as
    // "$24.75 per unit"; no "Save N%" line on the pack buttons (AJ 2026-10-11 22:47), so each stays two lines.
    // v5: printed only where the pack really is cheaper per unit than the single (see below).
    syncPacks(variant, plan) {
      let headUnit = null;
      this.groups.forEach((group, index) => {
        if (!group.hasAttribute('data-cq-pack-group')) return;
        const rows = [...group.querySelectorAll('input[data-cq-pack]')].map((input) => {
          const value = normInput(input);
          const wanted = norms(variant).slice();
          wanted[index] = value;
          const found = this.choiceVariant(wanted, index);
          const match = found && !found.callForPrice ? found : null;
          const count = Number(input.dataset.cqPack);
          const matchPlan = match && (plan || this.requiresPlan(match))
            ? match.plans.find((candidate) => String(candidate.id) === String(plan?.id)) || preferredPlan(match.plans, this.planGroups, match.categoryDefaultPlan) : null;
          const cents = match ? (matchPlan ? matchPlan.price : match.price) : null;
          return { input, count, match, unit: cents === null || !count ? null : Math.ceil(cents / count) };
        });
        // v5 (2026-10-24): the per-unit line prints only where a pack costs less per unit than the smallest pack (the
        // single) under the same purchase type, so it is always a real saving (same rule as the snippet).
        const base = rows.filter((row) => row.unit !== null).sort((a, b) => a.count - b.count)[0] || null;
        rows.forEach((row) => {
          const slot = row.input.closest('label').querySelector('[data-cq-pack-unit]');
          if (!slot) return;
          const selected = row.match?.id === variant.id;
          const money = row.unit !== null ? this.money(row.unit) : '';
          const unitText = money ? this.dataset.packUnit.replace('__PRICE__', money) : '';
          const unitLabel = money ? this.dataset.packUnitLabel.replace('__PRICE__', money) : '';
          if (selected && row.count > 1 && money) headUnit = { text: unitText, label: unitLabel };
          const shown = Boolean(money && base && row.unit < base.unit);
          const labelText = shown ? `, ${unitLabel}` : '';
          if (slot.textContent === (shown ? labelText + unitText : '')) return;
          const spoken = Object.assign(document.createElement('span'), { className: 'visually-hidden', textContent: labelText });
          const visible = Object.assign(document.createElement('span'), { textContent: unitText });
          visible.setAttribute('aria-hidden', 'true');
          slot.replaceChildren(...(shown ? [spoken, visible] : []));
        });
      });
      return headUnit;
    }

    syncQuantity(render = true) {
      const quantity = this.form.elements.quantity;
      const min = Number(quantity.min) || 1;
      const step = Number(quantity.step) || 1;
      const max = quantity.max ? Number(quantity.max) : null;
      let value = Number(quantity.value);
      if (!Number.isFinite(value) || value < min) value = min;
      value = min + Math.round((value - min) / step) * step;
      if (max) value = Math.min(max, value);
      if (String(value) !== quantity.value) quantity.value = value;
      this.querySelector('[data-cq-quantity="-1"]').disabled = value <= min;
      this.querySelector('[data-cq-quantity="1"]').disabled = Boolean(max && value + step > max);
      if (render) this.renderTotal();
    }
  });
})();

/* Buying guides (AJ 2026-10-14 02:21 to 02:22): one native modal dialog for every guide at every width, a centred
   panel on desktop and a sheet from the bottom on phones (the "Guides" rules in cq-option-picker.css). The panel stays
   in its product DOM so selected-size facts and accessible relationships keep updating. A modal is fixed to the
   viewport, so opening never scrolls the page; the shared lock (cq-scroll-lock.js) stops the page behind and keeps its
   scrollbar, so nothing moves. In-guide actions act exactly as the matching control on the page would: a Choose button
   clicks its option chip, an Add button its post-filter add-on card, the planning guide's first answer the page's own
   "most popular setup" link (snippets/cq-option-guide.liquid). */
(() => {
  if (customElements.get('cq-option-guide')) return;
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const same = (a, b) => String(a || '').replace(/\s+/g, ' ').trim().toLowerCase() === String(b || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const words = (element) => (element?.textContent || '').replace(/\s+/g, ' ').trim();
  customElements.define('cq-option-guide', class extends HTMLElement {
    connectedCallback() {
      if (this.cqEvents) return;
      this.button = this.querySelector('.cq-option-guide-button');
      this.tip = this.querySelector('[data-cq-guide-panel]');
      if (!this.button || !this.tip) return;
      this.cqEvents = new AbortController();
      const options = { signal: this.cqEvents.signal };
      // Guides open only through deliberate button activation, including native Enter/Space clicks.
      this.button.addEventListener('click', () => { if (this.isOpen()) this.hide(true); else this.show(); }, options);
      this.tip.addEventListener('click', (event) => this.act(event), options);
      // Escape: the browser's cancel event, with the same closing motion as the close button.
      this.tip.addEventListener('cancel', (event) => { event.preventDefault(); this.hide(true, true); }, options);
      this.tip.addEventListener('close', () => this.finish(), options);
      // A press on the scrim closes the guide; the click that follows is swallowed so nothing behind reacts to it.
      document.addEventListener('pointerdown', (event) => {
        if (!this.isOpen() || event.target !== this.tip) return;
        const box = this.tip.getBoundingClientRect();
        if (event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom) return;
        this.swallow = event.timeStamp;
        this.hide(true, true);
      }, { ...options, capture: true });
      document.addEventListener('click', (event) => {
        if (this.swallow && event.timeStamp - this.swallow < 800) { event.preventDefault(); event.stopPropagation(); }
        this.swallow = 0;
      }, { ...options, capture: true });
      this.closest('dialog')?.addEventListener('close', () => this.hide(), options);
      this.visibility = new MutationObserver(() => { if (this.closest('[hidden]')) this.hide(); });
      this.visibility.observe(this, { attributes: true, attributeFilter: ['hidden'] });
      this.watchFit();
    }

    disconnectedCallback() {
      this.hide();
      this.cqEvents?.abort();
      this.cqEvents = null;
      this.visibility?.disconnect();
      this.fitSize?.disconnect();
      this.fitText?.disconnect();
      this.fitSize = this.fitText = null;
      window.CQMeasure?.cancel(this);
    }

    // AJ 2026-10-21: a guide on an option heading shares the heading's line (cq-option-picker.css). When the heading
    // and the guide's words do not fit on one line, the guide is the round "?" alone (.cq-option-guide--fit); its words
    // stay its accessible name. Checked again when the buy box changes width or the chosen value in the heading changes.
    // The check runs in the shared post-paint measurement pass (CQMeasure) with the other guides and the picker's fit
    // checks: the fit class comes off in the clear step, the heading, the words and the group are read in the read
    // step, and the class and the heading's room for the control are written in the write step.
    watchFit() {
      const group = this.parentElement;
      if (!group?.matches('.cq-purchase-group, .cq-bold-toggles') || this.matches('.cq-option-guide--inline, .cq-option-guide--icon, .cq-option-guide--words')) return;
      const legend = group.querySelector(':scope > .cq-purchase-legend');
      if (!legend || typeof ResizeObserver === 'undefined') return;
      const measure = window.CQMeasure;
      const FIT = 'cq-option-guide--fit';
      // Still on this heading (a docked guide moves and connects again with a new watch) and shown.
      const live = () => this.isConnected && this.parentElement === group && !this.hidden;
      // The group width the observer last reported and the legend's words at the last measurement. A group that only
      // changed height (a note opening under it) or a legend written with the same words leaves the heading line as it
      // was, so neither is measured again; the fonts arriving always are.
      let width = null;
      let measured = null;
      let hadFit = false;
      const space = (buttonWidth) => {
        const value = `${Math.ceil(buttonWidth + 8)}px`;
        if (group.style.getPropertyValue('--cq-guide-space') !== value) group.style.setProperty('--cq-guide-space', value);
      };
      const entry = {
        // The words must show while the heading is measured; the class comes back in the write step when they do not fit.
        clear: () => {
          hadFit = this.classList.contains(FIT);
          if (hadFit && live()) this.classList.remove(FIT);
        },
        read: () => {
          if (!live() || !group.offsetWidth) return null;
          if (!this.closest('.cq-product-page')) {
            const line = parseFloat(getComputedStyle(legend).lineHeight) || 21;
            return { fit: legend.getBoundingClientRect().height > line * 1.5, button: null };
          }
          // The legend is nowrap with ellipsis, so its height cannot tell whether its full text fits.
          const range = document.createRange();
          range.selectNodeContents(legend);
          const textWidth = range.getBoundingClientRect().width;
          const buttonWidth = this.button.getBoundingClientRect().width;
          return { fit: textWidth + buttonWidth + 8 > group.clientWidth, button: buttonWidth };
        },
        write: (result) => {
          if (!result) {
            // Not measured this time (hidden, moved or not laid out): the class stays what it was.
            if (hadFit && live() && !this.classList.contains(FIT)) this.classList.add(FIT);
            return;
          }
          measured = { width, text: legend.textContent };
          if (this.classList.contains(FIT) !== result.fit) this.classList.toggle(FIT, result.fit);
          if (result.button === null) return;
          // The heading keeps room for the control as it now is: its words, or the round icon once the class is on,
          // which is read once every write of this pass has landed.
          if (result.fit) measure.after(() => this.button.getBoundingClientRect().width, space);
          else space(result.button);
        },
      };
      const schedule = (force = false) => {
        if (force) measured = null;
        else if (measured && measured.width === width && measured.text === legend.textContent) return;
        measure.queue(this, entry);
      };
      this.fitSize = new ResizeObserver((entries) => {
        width = entries[0].contentRect.width;
        schedule();
      });
      this.fitSize.observe(group);
      // v5.7: the observer's first callback already measures; the fonts.ready pass is only for fonts still arriving.
      if (document.fonts && document.fonts.status !== 'loaded') document.fonts.ready.then(() => { if (this.isConnected) schedule(true); });
      this.fitText = new MutationObserver(() => schedule());
      this.fitText.observe(legend, { childList: true, characterData: true, subtree: true });
      this.fitText.observe(this, { attributes: true, attributeFilter: ['hidden'] });
    }

    isOpen() { return Boolean(this.tip?.open); }
    component() { return this.closest('cq-purchase-options'); }

    show(opener = this.button) {
      if (!this.tip || this.closest('[hidden]')) return;
      clearTimeout(this.closeTimer);
      this.tip.classList.remove('is-closing');
      this.opener = opener?.isConnected ? opener : this.button;
      this.sync();
      if (!this.tip.open) {
        // Lock background input before opening; the fixed dialog and its scrollable body stay within the viewport.
        window.cqScrollLock?.lock(this, { root: this.tip, isOpen: () => Boolean(this.tip?.open) });
        try {
          this.tip.showModal();
        } catch (error) {
          this.finish();
          throw error;
        }
        this.view();
        this.media(true);
      }
      this.button.setAttribute('aria-expanded', 'true');
      (this.tip.querySelector('.cq-option-guide-title') || this.tip.querySelector('.cq-option-guide-close'))?.focus({ preventScroll: true });
    }

    // Closes at once, or with the closing motion (close button, Escape, the scrim) unless motion is reduced.
    hide(refocus = false, animate = false) {
      if (!this.tip?.open) { if (this.tip) this.finish(); return; }
      this.refocus = refocus;
      if (animate && !reduced()) {
        if (this.tip.classList.contains('is-closing')) return;
        this.tip.classList.add('is-closing');
        const done = () => {
          clearTimeout(this.closeTimer);
          this.tip?.removeEventListener('transitionend', ended);
          if (this.tip?.open) this.tip.close();
        };
        const ended = (event) => { if (event.target === this.tip && event.propertyName === 'transform') done(); };
        this.tip.addEventListener('transitionend', ended);
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(done, 300);
        return;
      }
      this.tip.close();
    }

    // Every close path ends here (the dialog's close event): unlock the page, reset the trigger, return focus.
    finish() {
      clearTimeout(this.closeTimer);
      this.tip?.classList.remove('is-closing');
      window.cqScrollLock?.unlock(this);
      this.media(false);
      this.button?.setAttribute('aria-expanded', 'false');
      const opener = this.opener?.isConnected ? this.opener : this.button;
      if (this.refocus && opener?.isConnected) opener.focus({ preventScroll: true });
      this.refocus = false;
      this.opener = null;
    }

    // An editor's video in the window (snippets/cq-option-guide-media.liquid, AJ 2026-10-15 05:57) waits in a
    // <template>, so the page loads no video bytes until the window opens. Opening puts it in place and plays it muted
    // and looped, unless the shopper asks for reduced motion (then the poster and the controls wait for them); closing
    // pauses it.
    media(open) {
      const slot = this.tip?.querySelector('[data-cq-guide-video]');
      if (!slot) return;
      const template = slot.querySelector(':scope > template');
      if (open && template) slot.replaceChildren(template.content.cloneNode(true));
      const video = slot.querySelector('video');
      if (!video) return;
      if (!open) { video.pause(); return; }
      if (reduced()) return;
      video.muted = true;
      video.play()?.catch(() => {});
    }

    // The option input a Choose button stands for: the chip with that value in this guide's own option group.
    pickInput(button) {
      const group = this.closest('[data-cq-option]');
      return [...(group?.querySelectorAll('input') || [])].find((input) => same(input.value, button.dataset.cqGuidePick));
    }

    // The post-filter add-on card a toggle stands for (cq-option-picker.js draws them from Bold's options).
    toggleInput(button) {
      let pattern;
      try { pattern = new RegExp(button.dataset.cqGuideToggle, 'i'); } catch (error) { return null; }
      return [...(this.component()?.querySelectorAll('[data-cq-toggle-input]') || [])]
        .find((input) => pattern.test(words(input.closest('label')?.querySelector('.cq-bold-toggle-name'))));
    }

    popularLink() {
      return [...(this.component()?.querySelectorAll('[data-cq-popular]') || [])].find((link) => !link.closest('cq-option-guide'));
    }

    // A chosen option or ticked add-on: aria-pressed, and the theme's tonal button (cq-design-tokens.css).
    press(button, on) {
      button.setAttribute('aria-pressed', String(Boolean(on)));
      button.classList.toggle('cq-btn-tonal', Boolean(on));
      button.classList.toggle('button-secondary', !on);
    }

    // Action states follow the page: chosen values read Selected, ticked add-ons Added, a missing control hides its
    // action, and an add-on sized for smaller systems hides above its size.
    sync() {
      const flow = Number(String(this.component()?.variant?.options?.[0] || '').replace(/,/g, '').match(/^([\d.]+)\s*GPD$/i)?.[1]) || 0;
      this.tip.querySelectorAll('[data-cq-guide-pick]').forEach((button) => {
        const input = this.pickInput(button);
        button.hidden = !input;
        this.press(button, input?.checked);
        const note = button.parentElement.querySelector('[data-cq-guide-pick-note]');
        if (!note) return;
        const text = input && !input.checked ? words(input.closest('label')?.querySelector('[data-cq-value-price-spoken]')) : '';
        note.textContent = text;
        note.hidden = !text;
      });
      this.tip.querySelectorAll('[data-cq-guide-toggle]').forEach((button) => {
        const input = this.toggleInput(button);
        const max = Number(button.dataset.cqGuideMaxGpd) || 0;
        button.hidden = !input || Boolean(max && flow > max);
        this.press(button, input?.checked);
        // The add-on's price as a plain amount, without the card's "+" (AJ dropped relative prices).
        const price = button.parentElement.querySelector('[data-cq-guide-toggle-price]');
        if (price) {
          price.textContent = input?.cqToggle?.price || words(input?.closest('label')?.querySelector('.cq-bold-toggle-price')).replace(/^\+\s*/, '');
          price.hidden = !price.textContent;
        }
      });
      const popular = this.tip.querySelector('[data-cq-guide-popular]');
      if (popular) {
        const link = this.popularLink();
        popular.hidden = !link;
        const text = popular.querySelector('[data-cq-guide-popular-text]');
        if (text) text.textContent = words(link);
      }
    }

    act(event) {
      if (event.defaultPrevented) return;
      if (event.target.closest('.cq-option-guide-close')) { this.hide(true, true); return; }
      const pick = event.target.closest('[data-cq-guide-pick]');
      if (pick) {
        const input = this.pickInput(pick);
        if (input && !input.checked) input.click();
        this.hide(true, true);
        return;
      }
      const toggle = event.target.closest('[data-cq-guide-toggle]');
      if (toggle) {
        // Several add-ons can be wanted, so the guide stays open and shows the new state.
        this.toggleInput(toggle)?.click();
        requestAnimationFrame(() => this.sync());
        return;
      }
      const view = event.target.closest('[data-cq-guide-view]');
      if (view) {
        event.preventDefault();
        this.view(view.dataset.cqGuideView)?.focus({ preventScroll: true });
        return;
      }
      if (event.target.closest('[data-cq-guide-popular-button]')) {
        this.popularLink()?.click();
        this.hide(true, true);
        return;
      }
      this.followGuideLink(event);
    }

    // A guide with two views (the size guide's production rates, data-cq-guide-pane) shows one at a time and opens on
    // the first. Returns the shown view's title, which takes focus when a shopper switches views.
    view(name) {
      const panes = [...this.tip.querySelectorAll('[data-cq-guide-pane]')];
      this.tip.querySelector('.cq-option-guide-body')?.scrollTo(0, 0);
      if (!panes.length) return null;
      const shown = name || panes[0].dataset.cqGuidePane;
      panes.forEach((pane) => { pane.hidden = pane.dataset.cqGuidePane !== shown; });
      return panes.find((pane) => !pane.hidden)?.querySelector('.cq-option-guide-title');
    }

    // Links between guides switch within the same purchase component and keep the original trigger for focus return.
    followGuideLink(event) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('a[data-cq-guide-link]');
      if (!link || !this.tip.contains(link) || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      const component = this.component();
      const guides = Array.from(component?.querySelectorAll('cq-option-guide') || [])
        .filter(guide => guide.closest('cq-purchase-options') === component);
      const destination = guides.find(guide => guide.getAttribute('data-cq-guide') === link.getAttribute('data-cq-guide-link'));
      if (!destination?.button || !destination.tip || !destination.isConnected || destination.closest('[hidden]')
        || typeof destination.show !== 'function' || link.getAttribute('href') !== `#${destination.tip.id}`) return;
      event.preventDefault();
      if (destination === this) return;
      const opener = this.opener?.isConnected ? this.opener : this.button;
      // Close before opening; never leave an inert source-panel link as the new dialog's return target.
      guides.forEach(guide => { if (guide.isOpen?.()) guide.hide(); });
      opener.focus({ preventScroll: true });
      destination.show(opener);
    }
  });
})();
